<template>
  <div class="psl-page">
    <APageHead title="결제 내역 관리" sub="학습자 결제 및 구독 내역을 관리합니다.">
      <template #actions>
        <ABtn icon="card_giftcard" @click="openFreeItemModal">무료/사은품 등록</ABtn>
        <ABtn icon="payments" @click="openPgFeeModal">PG 수수료 정보</ABtn>
        <ABtn icon="refresh" @click="refresh">새로고침</ABtn>
        <ABtn kind="primary" icon="download" @click="downloadExcel">엑셀 다운로드</ABtn>
      </template>
    </APageHead>

    <!-- KPI -->
    <div class="psl-page__stats">
      <ACard>
        <!--
          매출 KPI 의 amount 는 환불 미차감입니다(docs/결제_영구소유_개편_관리자화면_가이드.md §3.3). 라벨에 그 사실을 박아 두고,
          서버가 refund_amount·net_amount 를 내려주면(운영 미배포) 순액을 작게 병기합니다.
          순액의 기간 귀속은 결제일 기준 — 8월 결제가 9월에 환불되면 8월에서 빠집니다. 달력 합계와 같은 기준.
        -->
        <AStat
          label="총 매출 (수수료 포함 · 환불 미차감)"
          :value="formatCurrency(stats.totalRevenue?.amount, stats.totalRevenue?.currency)"
        >
          <small class="psl-page__stat-note">환율에 따라 변동될 수 있습니다.</small>
          <small v-if="revenueNet(stats.totalRevenue)" v-tooltip :title="REVENUE_NET_TIP" class="psl-page__stat-net">
            환불 -{{ formatCurrency(stats.totalRevenue.refund_amount, stats.totalRevenue.currency) }}
            · 순액 {{ formatCurrency(revenueNet(stats.totalRevenue), stats.totalRevenue.currency) }}
          </small>
        </AStat>
      </ACard>
      <ACard>
        <AStat
          label="이번 달 매출 (수수료 포함 · 환불 미차감)"
          :value="formatCurrency(stats.monthRevenue?.amount, stats.monthRevenue?.currency)"
        >
          <small class="psl-page__stat-note">환율에 따라 변동될 수 있습니다.</small>
          <small v-if="revenueNet(stats.monthRevenue)" v-tooltip :title="REVENUE_NET_TIP" class="psl-page__stat-net">
            환불 -{{ formatCurrency(stats.monthRevenue.refund_amount, stats.monthRevenue.currency) }}
            · 순액 {{ formatCurrency(revenueNet(stats.monthRevenue), stats.monthRevenue.currency) }}
          </small>
        </AStat>
      </ACard>
      <ACard>
        <AStat label="활성 도서(영구 포함)" :value="stats.activeSubscriptions?.count || 0" unit="건">
          <!-- 도서가 영구 소유가 되면서 지표 의미가 바뀌었습니다. 마이그레이션으로 기존 자연만료 건도 ACTIVE 로 복귀합니다. -->
          <small class="psl-page__stat-note psl-page__stat-note--muted">영구 전환된 유료 결제 + 미만료 무료 지급분</small>
        </AStat>
      </ACard>
      <ACard>
        <AStat label="총 거래" :value="stats.totalTransactions?.count || 0" unit="건">
          <!--
            건수는 테스트 결제를 포함하고 매출은 제외합니다. 기준이 다른 게 정상입니다.
            적어 두지 않으면 매출 ÷ 거래로 객단가를 내볼 때 낮게 나와 "매출 누락"으로 읽힙니다.
          -->
          <small class="psl-page__stat-note psl-page__stat-note--muted">테스트 결제 포함 · 매출 KPI 는 테스트 제외</small>
        </AStat>
      </ACard>
      <!--
        activeFeatures 는 백엔드 개편 배포 후에만 내려옵니다.
        없을 때 0 을 찍으면 "기능 이용권이 0건"이라는 오보가 되므로 카드 자체를 그리지 않습니다.
      -->
      <ACard v-if="hasActiveFeatures">
        <AStat label="활성 기능 이용권" :value="stats.activeFeatures" unit="건">
          <small class="psl-page__stat-note psl-page__stat-note--muted">standard/premium 기간제 이용권</small>
        </AStat>
      </ACard>
    </div>

    <!-- 탭 + 보기 전환 -->
    <div class="psl-page__tabs-row">
    <div class="p-tabs psl-page__tabs">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        :class="['p-tabs__tab', { 'is-active': activeTab === tab.value }]"
        v-tooltip="tab.hint || ''"
        @click="handleTabChange(tab.value)"
      >
        <i class="material-icons-round psl-page__tab-icon">{{ tab.icon }}</i>
        {{ tab.label }}
        <span v-if="tab.count > 0" class="p-tabs__count">{{ tab.count }}</span>
      </button>
    </div>
    <!-- 목록·달력은 같은 검색 조건과 탭을 공유합니다. 달력은 결제일 기준으로 한 달을 통째로 받아 묶습니다. -->
    <div class="p-tabs psl-page__view-switch" role="tablist" aria-label="보기 방식">
      <button
        v-for="v in viewModes"
        :key="v.value"
        type="button"
        :class="['p-tabs__tab', { 'is-active': viewMode === v.value }]"
        @click="setViewMode(v.value)"
      >
        <i class="material-icons-round psl-page__tab-icon">{{ v.icon }}</i>
        {{ v.label }}
      </button>
    </div>
    </div>

    <!-- 검색 -->
    <ACard overflow="visible">
      <SearchArea ref="searchArea" :search-fields="searchFields" @search="handleSearch" />
    </ACard>

    <!-- 결제 내역 -->
    <ACard>
      <template #title>
        <span class="psl-page__sec-title">
          <i class="material-icons-round psl-page__sec-icon">receipt_long</i>
          결제 내역
        </span>
      </template>
      <template #action>
        <label class="psl-page__exclude-toggle">
          <input type="checkbox" v-model="excludeFree" @change="handleExcludeFreeChange" />
          <i class="material-icons-round">card_giftcard</i>
          무료/사은품 제외
        </label>
        <APill v-if="headCount > 0" kind="info">총 {{ headCount }}건</APill>
      </template>

      <!--
        refund_status 는 백엔드 배포 후에만 동작합니다. 미배포 서버는 모르는 쿼리 파라미터를
        조용히 무시하므로 필터를 걸어도 전체 목록이 그대로 나오고, 화면상으로는 "필터가 먹은"
        것과 구분되지 않습니다. 그 상태로 목록을 읽으면 미환불 건까지 환불 건으로 오독하게 되어
        응답 자체로 검증합니다(가이드 §3.1 이 서버 쪽에서 같은 사고를 막는 것과 같은 이유).
      -->
      <template v-if="viewMode === 'list'">
      <div v-if="refundFilterIgnored" class="psl-page__filter-warn">
        <i class="material-icons-round">warning</i>
        <div>
          <strong>환불 여부 필터가 적용되지 않았습니다.</strong>
          응답에 조건과 어긋나는 행이 섞여 있습니다 —
          <code>refund_status</code> 가 아직 배포되지 않은 서버로 보입니다.
          이 목록을 환불 건으로 읽지 마세요.
        </div>
      </div>

      <!--
        조회 조건 전체의 통화별 합계입니다. 현재 페이지가 아니라 total_count 와 같은 모수입니다
        (docs/결제_영구소유_개편_관리자화면_가이드.md §3.2: 서버가 같은 WHERE 로 집계). 응답에 amount_totals 가 없으면(미배포 서버)
        줄 자체를 숨깁니다 — 현재 페이지 10건을 더해서 대신 보여주면 "전체 합계"로 오독됩니다.
        통화가 다른 금액은 합치지 않고 나란히 둡니다(환율 환산은 범위 밖).
      -->
      <div v-if="amountTotals.length > 0" class="psl-page__totals">
        <span class="psl-page__totals-label">
          <i class="material-icons-round">functions</i>
          조회 합계
        </span>
        <span v-for="t in amountTotals" :key="t.currency_code" class="psl-page__totals-item">
          <strong class="psl-page__totals-amount">{{ formatCurrency(t.amount, t.currency_code) }}</strong>
          <small class="psl-page__totals-count">{{ t.count }}건</small>
          <!-- 환불이 있을 때만 순액을 덧붙입니다. 순액 = 결제금액 − 환불금액(같은 결제 통화 기준). -->
          <small v-if="Number(t.refund_amount) > 0" class="psl-page__totals-refund">
            환불 -{{ formatCurrency(t.refund_amount, t.currency_code) }}
            · 순액 {{ formatCurrency(netAmount(t), t.currency_code) }}
          </small>
        </span>
      </div>

      <AEmpty
        v-if="tableData.length === 0"
        title="결제 내역이 없어요"
        sub="다른 필터를 시도하거나 무료/사은품 등록으로 데이터를 추가해보세요."
      />

      <ATable v-else :columns="columns" :rows="tableData" row-key="payment_idx">
        <template #cell:select="{ row }">
          <input type="checkbox" :value="row.payment_idx" v-model="selectedItems" />
        </template>
        <template #cell:no="{ index }">
          <span class="psl-page__num">{{ getItemNumber(index) }}</span>
        </template>
        <template #cell:learner="{ row }">
          <div class="psl-page__learner-cell">
            <span class="psl-page__learner">{{ row.learner_name }}</span>
            <!--
              같은 이름의 서로 다른 계정이 실제로 존재합니다(운영에서 "중복 지급"으로 오인된 사례).
              이름만으로는 구분할 단서가 없어 이메일을 함께 찍습니다.
              백엔드 배포 전에는 필드가 없으므로 빈 줄을 만들지 않습니다.
            -->
            <small v-if="row.learner_email" class="psl-page__learner-email">{{ row.learner_email }}</small>
          </div>
        </template>
        <template #cell:textbook="{ row }">
          <div class="psl-page__textbook">
            <div class="psl-page__textbook-title">{{ row.textbook_title }}</div>
            <small class="psl-page__mono">{{ row.barcode }}</small>
            <small v-if="row.order_id" class="psl-page__order-id">Order ID: {{ row.order_id }}</small>
          </div>
        </template>
        <template #cell:product="{ row }">
          <div class="psl-page__product">
            <!--
              백엔드 개편 배포 전에는 purchase_type·grade 가 없습니다.
              없는 값을 'new' 로 추정해 뱃지를 찍으면 데이터가 있는 것처럼 보이므로 '—' 로 둡니다.
            -->
            <APill v-if="hasPurchaseType(row)" :kind="purchaseTypePillKind(row.purchase_type)">
              <i class="material-icons-round psl-page__method-icon">{{ purchaseTypeIcon(row.purchase_type) }}</i>
              {{ purchaseTypeLabel(row.purchase_type) }}
            </APill>
            <APill v-if="gradeLabel(row.grade)" :kind="gradePillKind(row.grade)">{{ gradeLabel(row.grade) }}</APill>
            <!--
              테스트 결제는 종전에 서버가 목록에서 빼 버려 "기록이 없다"는 오안내를 낳았습니다.
              이제 내려오므로 숨기지 않고 구분만 합니다. dark 는 이 표에서 유일하게 안 쓰던 색입니다.
            -->
            <APill
              v-if="isTestRow(row)"
              kind="dark"
              v-tooltip
              title="샌드박스·QA 테스트 결제입니다. 매출 KPI 에는 집계되지 않습니다."
            >테스트 결제</APill>
            <span
              v-if="!hasPurchaseType(row) && !gradeLabel(row.grade) && !isTestRow(row)"
              class="psl-page__muted"
            >—</span>
          </div>
        </template>
        <template #cell:amount="{ row }">
          <div class="psl-page__amount-cell">
            <span class="psl-page__amount">
              {{ formatCurrency(row.amount, row.currency_code) }}
            </span>
            <!-- 무료 지급 건에 남아 있는 옛 기본값(19.99)을 "할인해 준 금액"으로 오해하지 않도록 가립니다 -->
            <small v-if="visibleCouponAmount(row) > 0" class="psl-page__discount">
              -{{ formatCurrency(visibleCouponAmount(row), row.currency_code) }}
            </small>
            <!--
              업그레이드 차감(2차답변 §4). 쿠폰과 나란히 서지만 성격이 달라 라벨을 붙입니다 —
              라벨이 없으면 두 줄이 모두 쿠폰 할인으로 읽히고, 실제로는 쿠폰을 안 쓴 결제입니다.
              이 줄이 없으면 정가보다 작은 금액을 설명할 수단이 화면에 하나도 없습니다.
            -->
            <small v-if="visibleCreditedAmount(row) > 0" class="psl-page__credited" v-tooltip :title="CREDITED_HINT">
              업그레이드 차감 -{{ formatCurrency(visibleCreditedAmount(row), row.currency_code) }}
            </small>
          </div>
        </template>
        <template #cell:method="{ row }">
          <APill kind="default">
            <i class="material-icons-round psl-page__method-icon">{{ getPaymentMethodIcon(row.payment_method) }}</i>
            {{ getPaymentMethodLabel(row.payment_method) }}
          </APill>
          <!--
            V 는 기관이 실제로 낸 돈(기관가)이라 매출 KPI·달력·조회 합계·정산에 모두 들어갑니다(docs/바우처_기관발행_관리자화면_가이드.md §5).
            학생 결제로 오독하지 않게 여기서 갈라 둡니다. 매출 인식 시점은 학생 등록 시점입니다.
          -->
          <small v-if="row.payment_method === 'V'" class="psl-page__method-note">학생 결제 아님 · 기관 부담</small>
        </template>
        <template #cell:date="{ row }">
          <span class="psl-page__mono">{{ formatDateTime(row.payment_date) }}</span>
        </template>
        <template #cell:period="{ row }">
          <div class="psl-page__period">
            <!-- plan 이 없는 결제(앱스토어 경로의 basic 재구매 등)는 기간 자체가 없습니다 -->
            <span v-if="hasNoPlan(row)" class="psl-page__muted">—</span>
            <template v-else>
              <span class="psl-page__mono">{{ formatDate(row.start_date) }}</span>
              <small :class="{ 'psl-page__period-perm': isPermanentRow(row) }">~ {{ endDateText(row) }}</small>
              <!-- 업그레이드 결제의 기간은 도서가 아니라 기능 이용 기간입니다 -->
              <small v-if="isUpgradeRow(row)" class="psl-page__period-note">기능 이용 기간</small>
              <!-- new + std/prem 은 도서(영구)와 기능 만료일이 달라 따로 적어 줍니다 -->
              <small v-else-if="featurePeriodNote(row)" class="psl-page__period-feature">
                {{ featurePeriodNote(row) }}
              </small>
              <!--
                묶음 구매의 기능만 끝난 행(2차답변 §4-1). 위 부기가 그냥 사라지므로
                적어 두지 않으면 "이 결제엔 기능이 없었다"로 읽힙니다.
              -->
              <small v-else-if="isFeatureEndedRow(row)" class="psl-page__period-ended" v-tooltip :title="FEATURE_ENDED_HINT">
                {{ FEATURE_ENDED_TEXT }}
              </small>
            </template>
          </div>
        </template>
        <template #cell:status="{ row }">
          <div class="psl-page__status-cell">
            <!--
              환불 판정은 refund_at 하나로 합니다. grade 는 "구매한 상품의 등급"이라
              환불돼도 바뀌지 않아, 앱팀이 그 값으로 환불 미반영을 오판정한 사례가 있습니다.
            -->
            <APill v-if="isRefunded(row)" kind="err" dot>환불</APill>
            <APill v-else :kind="getRentalStatusPillKind(row.rental_status)" dot>
              {{ getRentalStatusLabel(row.rental_status) }}
            </APill>
            <!--
              상위 등급 전환으로 끝난 이용권은 '만료'인데 종료일만 미래로 남습니다(정책 §5).
              그대로 두면 "만료라는데 날짜는 남았다" → 환불 사고로 읽혀 CS 가 틀어지므로
              서버 값(만료)은 그대로 두고 사유만 덧붙입니다.
            -->
            <APill v-if="isSupersededRow(row)" kind="info" v-tooltip :title="supersededTitle(row)">
              {{ SUPERSEDED_TEXT }}
            </APill>
            <template v-if="isRefunded(row)">
              <!-- 언제 반영됐는지가 CS 응대의 첫 질문인데 종전에는 목록에 아예 없었습니다. -->
              <small class="psl-page__refund-at">{{ formatDateTime(row.refund_at) }}</small>
              <!-- 부분 환불이면 결제금액과 다릅니다. 통화도 결제와 별개로 내려옵니다. -->
              <small v-if="row.refund_amount != null" class="psl-page__refund-amount">
                {{ formatCurrency(row.refund_amount, row.refund_currency_code || row.currency_code) }}
                <!--
                  LS 는 구매자 국가의 세금을 더해 청구하고 환불 때 함께 돌려주는데, 우리 기록은
                  세전 기준입니다(docs/환불_즉시반영_관리자화면_가이드.md §3.3). CS 응대에서 가장 먼저 읽는 숫자가 이 칸이라
                  환불 실행 모달의 안내만으로는 늦습니다 — 여기서 바로 갈라 둡니다.
                -->
                <span
                  v-if="row.payment_method === 'L'"
                  class="psl-page__refund-pretax"
                  v-tooltip
                  :title="LS_PRETAX_HINT"
                >세전</span>
              </small>
            </template>
          </div>
        </template>
        <template #cell:actions="{ row }">
          <div class="psl-page__actions">
            <button class="p-icon-btn" type="button" v-tooltip title="인보이스 보기" @click="openInvoiceModal(row)">
              <i class="material-icons-round">receipt</i>
            </button>
            <button
              v-if="(row.payment_method === 'T' || row.payment_method === 'L') && row.sale_invoice_idx"
              class="p-icon-btn"
              type="button"
              v-tooltip
              title="PDF 다운로드"
              @click="downloadInvoicePDF(row.sale_invoice_idx)"
            >
              <i class="material-icons-round">download</i>
            </button>
            <!--
              환불 "실행" — 우리 서버가 결제사(Toss/LemonSqueezy)에 취소를 겁니다.
              실제로 돈이 움직이고 되돌릴 수 없어 확인 대화상자를 반드시 거칩니다.
              A/G 는 사용자가 스토어에서 직접 환불하므로 실행 API 자체가 없습니다(가이드 §3.3).

              결제일로부터 14일이 지난 T/L 행은 서버가 409 로 거부하므로(docs/환불_즉시반영_관리자화면_가이드.md §3.3) 버튼을
              **감추지 않고 잠급니다** — 감추면 A/G 처럼 "스토어에서 환불" 로 읽히고, 잠가 두면
              "환불 대상이지만 기한이 지났다"가 그 자리에서 읽힙니다. 관리자 재량 예외는 없어
              "그래도 환불" 류의 우회 버튼은 두지 않습니다. 정본은 서버 판정입니다(자정 경계 등).

              disabled 버튼은 마우스 이벤트를 내지 않아 v-tooltip 이 뜨지 않습니다(canRefundSync 주석).
              그래서 툴팁은 감싼 span 에 걸고, 버튼은 pointer-events 를 끊어 포인터가 span 에 닿게 합니다.
            -->
            <span
              v-if="canRefundExec(row) && isRefundWindowClosed(row)"
              class="psl-page__act-locked"
              v-tooltip
              :title="refundWindowClosedText(row)"
            >
              <button class="p-icon-btn p-icon-btn--danger" type="button" disabled>
                <i class="material-icons-round">money_off</i>
              </button>
            </span>
            <button
              v-else-if="canRefundExec(row)"
              class="p-icon-btn p-icon-btn--danger"
              type="button"
              v-tooltip
              :title="`${getPaymentMethodLabel(row.payment_method)} 환불 실행 — 결제사에 취소를 요청합니다`"
              @click="openRefundExecModal(row)"
            >
              <i class="material-icons-round">money_off</i>
            </button>
            <!--
              기관 바우처(V)는 환불 대상이 아닙니다 — 서버가 409 VOUCHER_PAYMENT_NOT_REFUNDABLE 로 거부합니다
              (docs/바우처_기관발행_관리자화면_가이드.md §5). 기관 돈은 학생 결제가 아니라 기관 잔액의 반환으로 정리합니다.
              A/G 처럼 버튼을 감추기만 하면 "스토어에서 환불" 로 읽히므로, 14일 경과 행과 같은 방식으로 잠근 버튼과 사유를 둡니다.
            -->
            <span
              v-if="row.payment_method === 'V'"
              class="psl-page__act-locked"
              v-tooltip
              title="기관 바우처 결제는 환불할 수 없습니다. 기관 돈은 바우처 › 기관 관리 › 잔액에서 반환으로 정리합니다."
            >
              <button class="p-icon-btn p-icon-btn--danger" type="button" disabled>
                <i class="material-icons-round">money_off</i>
              </button>
            </span>
            <!--
              환불 "반영" — 이미 스토어에서 환불된 건을 서버에 기록만 합니다. 안전하고 멱등이라
              확인 대화상자를 두지 않습니다. 실행 버튼과 나란히 서므로 아이콘·툴팁으로 구분합니다.
              refund_at 으로 더 좁히지 않는 이유는 canRefundSync 주석에 적어 두었습니다.
            -->
            <button
              v-if="canRefundSync(row)"
              class="p-icon-btn"
              type="button"
              :disabled="refundSyncBusyIdx === row.payment_idx"
              v-tooltip
              :title="refundSyncTooltip(row)"
              @click="runRefundSync(row)"
            >
              <i
                class="material-icons-round"
                :class="{ 'psl-page__sync-spin': refundSyncBusyIdx === row.payment_idx }"
              >sync</i>
            </button>
            <!--
              무료/사은품(F)은 잘못 등록해도 되돌릴 수단이 없어 삭제 버튼을 답니다.
              가드는 payment_method === 'F' 하나뿐입니다. refund_at·rental_status 로 더 좁히면
              정작 "잘못 등록한 활성 건"을 못 지우게 되어 기능이 무의미해집니다.
            -->
            <button
              v-if="row.payment_method === 'F'"
              class="p-icon-btn p-icon-btn--danger"
              type="button"
              v-tooltip
              title="무료/사은품 삭제"
              @click="openFreeDeleteModal(row)"
            >
              <i class="material-icons-round">delete_forever</i>
            </button>
          </div>
        </template>
      </ATable>

      <APagination
        v-if="totalCount > 0"
        :current-page="currentPage"
        :total="totalCount"
        :page-size="itemsPerPage"
        @change="(p) => { currentPage = p; getData(); }"
      />
      </template>

      <!--
        달력 뷰. 서버 집계(GET /payment/admin/paymentCalendar, docs/결제_영구소유_개편_관리자화면_가이드.md §3.2-1)를
        날짜별로 그립니다. 응답은 최대 31일 × 통화 수라 결제가 늘어도 화면이 받는 양은 같습니다.
        날짜는 Date 로 파싱하지 않고 문자열 앞 10자리를 그대로 씁니다 — 서버가 시간대 변환 없이
        DATE(created_at) 로 묶어 주므로, 여기서 변환하면 오히려 목록의 결제일과 칸이 어긋납니다.
      -->
      <div v-else class="psl-page__cal">
        <div class="psl-page__cal-nav">
          <ABtn icon="chevron_left" icon-only size="sm" title="이전 달" @click="shiftMonth(-1)" />
          <strong class="psl-page__cal-month">{{ calMonthLabel }}</strong>
          <ABtn icon="chevron_right" icon-only size="sm" title="다음 달" @click="shiftMonth(1)" />
          <ABtn size="sm" @click="goThisMonth">이번 달</ABtn>
          <span v-if="calLoading" class="psl-page__cal-summary psl-page__cal-summary--muted">불러오는 중…</span>
          <!--
            달력은 "그날(이 달) 실제로 들어온 돈"을 보는 화면이라 굵은 숫자는 순액(결제 − 환불)입니다.
            목록 뷰의 조회 합계가 결제금액을 굵게 두는 것과 반대이므로, 환불이 있으면 결제·환불을
            작게 병기해 두 화면의 숫자가 왜 다른지 바로 보이게 합니다.
          -->
          <span v-else-if="calCount > 0" class="psl-page__cal-summary">
            <strong>{{ calCount }}건</strong>
            <span v-for="a in calTotals" :key="a.currency_code" class="psl-page__cal-summary-item">
              <span class="psl-page__cal-summary-amt">{{ formatCurrency(a.net, a.currency_code) }}</span>
              <small v-if="a.refund > 0" class="psl-page__cal-summary-refund">
                결제 {{ formatCurrency(a.amount, a.currency_code) }} · 환불 -{{ formatCurrency(a.refund, a.currency_code) }}
              </small>
            </span>
          </span>
          <span v-else class="psl-page__cal-summary psl-page__cal-summary--muted">이 달에는 결제가 없습니다.</span>
        </div>

        <div class="psl-page__cal-grid">
          <div v-for="d in calDow" :key="d" class="psl-page__cal-dow">{{ d }}</div>
          <div
            v-for="cell in calCells"
            :key="cell.key"
            :class="['psl-page__cal-cell', {
              'is-out': !cell.inMonth,
              'is-today': cell.isToday,
              'has-data': !!cell.day,
            }]"
            v-tooltip
            :title="cell.day ? cellTitle(cell) : null"
            @click="cell.day && pickDay(cell.date)"
          >
            <span class="psl-page__cal-date">{{ cell.d }}</span>
            <template v-if="cell.day">
              <span class="psl-page__cal-count">{{ cell.day.count }}건</span>
              <!--
                칸의 금액은 순액(결제 − 환불)입니다. 그날 결제분 중 환불된 돈은 실제 소득이 아니므로
                결제금액을 그대로 보이면 매출을 부풀려 읽게 됩니다. 결제금액 원본은 툴팁(cellTitle)에 남깁니다.
                통화가 섞이면 두 줄까지만 보이고 나머지는 툴팁으로 넘깁니다. 환산해서 합치지 않습니다.
              -->
              <span
                v-for="a in cell.day.amounts.slice(0, 2)"
                :key="a.currency_code"
                class="psl-page__cal-amt"
              >{{ formatCurrency(a.net, a.currency_code) }}</span>
              <span v-if="cell.day.amounts.length > 2" class="psl-page__cal-more">
                +{{ cell.day.amounts.length - 2 }} 통화
              </span>
              <!-- 환불 건수 뒤에 통화별 환불금액을 붙입니다(환불이 있는 통화만, 표시 상한 2줄과 무관하게 전부). -->
              <span v-if="cell.day.refunds > 0" class="psl-page__cal-refund">
                환불 {{ cell.day.refunds }}건<template v-for="a in cell.day.amounts" :key="a.currency_code"><template v-if="a.refund > 0"> · -{{ formatCurrency(a.refund, a.currency_code) }}</template></template>
              </span>
            </template>
          </div>
        </div>
      </div>
    </ACard>

    <!-- 인보이스 모달 (자식) -->
    <InvoiceDetailModal
      :visible="showInvoiceModal"
      :payment="selectedPayment"
      :details="invoiceDetails"
      @close="closeInvoiceModal"
      @download-pdf="downloadInvoicePDF"
    />

    <!--
      환불 실행 모달 (Toss / LemonSqueezy)

      "환불 실행"은 우리 서버가 결제사에 취소를 걸어 실제로 돈을 돌려주는 동작입니다.
      되돌릴 수 없으므로 확인 대화상자를 반드시 거칩니다 — 조회만 하는 "환불 즉시 반영"
      (refundSync)과 성격이 정반대라 화면에서도 다른 모달로 분리해 둡니다(가이드 §3.3).
      두 결제사는 보내는 식별자만 다르고(payment_key vs payment_idx) 나머지 흐름이 같아
      한 모달을 공유합니다.
    -->
    <AModal v-model="showRefundExecModal" size="md" :close-on-backdrop="!refundExecLoading">
      <template #title>
        <span class="psl-page__modal-title">
          <i class="material-icons-round psl-page__modal-icon psl-page__modal-icon--danger">money_off</i>
          {{ refundExecMethodLabel }} 결제 환불 실행
        </span>
      </template>
      <template #sub>결제사에 취소를 요청합니다. 실행하면 되돌릴 수 없습니다.</template>

      <div v-if="refundExecItem" class="psl-page__cancel">
        <!--
          환불 기한 14일 (docs/환불_즉시반영_관리자화면_가이드.md §3.3 "환불 기한 14일")

          목록에서는 버튼이 잠겨 여기까지 못 오지만, 순서 가드의 "이 결제부터 환불" 로 넘어온
          상위 결제는 기한이 지났을 수 있습니다. 취소 사유를 다 적은 뒤에 409 를 받게 두지 않고
          모달을 연 자리에서 알리고 실행 버튼을 잠급니다. 서버 문구를 흉내 내지 않습니다 —
          아직 서버에 물어본 적이 없으므로 화면 문장으로 적습니다(순서 가드와 같은 원칙).
        -->
        <div v-if="refundWindowBlock" class="psl-page__cancel-warn psl-page__cancel-warn--hard">
          <i class="material-icons-round">event_busy</i>
          <div class="psl-page__guard">
            <strong>환불 가능 기간이 지났습니다.</strong>
            <p class="psl-page__guard-detail">
              결제일 {{ refundWindowBlock.paymentDate }} · 환불 기한 {{ refundWindowBlock.deadline }}
            </p>
            <p>
              이용약관상 환불은 <strong>결제일로부터 {{ REFUND_WINDOW_DAYS }}일 이내</strong>만 가능하며,
              기한이 지난 건은 서버가 <strong>409 로 거부</strong>합니다. 결제사에는 아무 요청도 가지 않습니다.
              관리자 재량 예외는 없습니다.
            </p>
          </div>
        </div>

        <!--
          환불 순서 가드 409 (docs/결제_등급업그레이드_차감정책.md §6-3)

          이 결제의 남은 가치가 이미 상위 등급 전환 결제에서 깎여 나갔으면 서버가 409 로 막습니다.
          다른 409 와 달리 **다음에 할 일이 있는** 실패라 토스트로 흘려보내면 안 됩니다 —
          안내 문장 안에 먼저 환불할 결제의 pay_code 가 들어 있는데 몇 초 뒤 사라지기 때문입니다.
          운영자가 사유를 못 읽으면 재시도만 반복하다 결국 결제사 대시보드에서 직접 취소하게 되고,
          그러면 가드를 우회한 것이라 차감분 회수가 사후 처리로 밀립니다(정책이 못박은 실패 시나리오).
          그래서 모달을 열어 둔 채, 서버 문구를 **그대로** 붙여 둡니다.
        -->
        <div v-if="refundOrderBlock" class="psl-page__cancel-warn psl-page__cancel-warn--hard">
          <i class="material-icons-round">block</i>
          <div class="psl-page__guard">
            <strong v-if="refundOrderBlock.confirmed">이 결제는 아직 환불할 수 없습니다.</strong>
            <strong v-else>이 결제를 먼저 환불하면 서버가 막습니다.</strong>

            <!--
              서버가 실제로 막은 경우에는 그 detail 을 **그대로** 씁니다(요청 §2).
              모달을 열자마자 미리 알린 경우에는 아직 서버에 물어본 적이 없으므로
              서버 문구를 흉내 내지 않고 화면 문장으로 적습니다 — 안 그러면 받지도 않은
              응답을 인용한 것처럼 보입니다.
            -->
            <p v-if="refundOrderBlock.confirmed" class="psl-page__guard-detail">{{ refundOrderBlock.detail }}</p>
            <p v-else class="psl-page__guard-detail">
              이 결제의 잔여 가치는 상위 등급 전환 결제에서 이미 차감됐습니다({{ SUPERSEDED_TEXT }}).
              그대로 환불을 실행하면 서버가 <strong>409 로 거부</strong>합니다 — 상위 결제를 먼저 환불해 주세요.
            </p>

            <div v-if="refundOrderBlock.payCode" class="psl-page__guard-code">
              <span>먼저 환불할 결제</span>
              <code>{{ refundOrderBlock.payCode }}</code>
              <button class="p-icon-btn" type="button" v-tooltip title="결제 코드 복사" @click="copyPayCode(refundOrderBlock.payCode)">
                <i class="material-icons-round">content_copy</i>
              </button>
              <!--
                같은 학습자·교재의 미환불 결제는 이미 아래에 받아 두었습니다.
                그 안에 해당 pay_code 가 있으면 검색으로 되찾게 하지 않고 바로 넘겨 줍니다
                (결제 코드로 찾는 검색 필드가 이 화면에 없습니다).
              -->
              <ABtn
                v-if="supersedingPayment"
                kind="danger-fill" icon="money_off"
                @click="openRefundExecModal(supersedingPayment)"
              >이 결제부터 환불</ABtn>
            </div>

            <!--
              "돈은 안 움직였다"는 409 를 실제로 받았을 때만 할 수 있는 말입니다(가드가 PG 취소 호출
              **전** 지점이라 - 요청 §2). 미리 알린 경우에 같이 적으면 하지도 않은 시도를 안심시키는
              문장이 되므로 나누어 둡니다.
            -->
            <p v-if="refundOrderBlock.confirmed">
              결제사에 취소를 요청하기 <strong>전</strong>에 막혔습니다 —
              <strong>돈은 전혀 움직이지 않았습니다.</strong>
              위 결제를 먼저 환불하면 이 이용권이 원래 만료일로 되살아나고, 그다음 정상 환불됩니다.
            </p>
            <p v-else>
              위 결제를 먼저 환불하면 이 이용권이 원래 만료일로 되살아나고, 그다음 정상 환불됩니다.
              <!--
                버튼을 잠그지는 않습니다. 이 안내는 목록을 띄운 시점의 값이라, 그사이 상위 결제가
                다른 경로로 환불돼 이 결제가 되살아났을 수 있습니다(백엔드도 그래서 서버 409 를
                남겨 달라고 했습니다 - 2차답변 §5). 잠그면 정당한 환불을 화면이 막게 됩니다.
              -->
              <strong>판정은 서버가 합니다</strong> — 상황이 바뀌었다고 판단되면 그대로 실행해 보셔도 됩니다.
            </p>
            <p>
              토스·LemonSqueezy 대시보드에서 직접 취소하지 마세요.
              이 순서 가드를 건너뛰게 되어 차감분 회수가 사후 처리로 밀립니다.
            </p>
          </div>
        </div>

        <div class="psl-page__cancel-summary">
          <div class="psl-page__cancel-row">
            <span>학습자</span>
            <strong>
              {{ refundExecItem.learner_name }}
              <small v-if="refundExecItem.learner_email" class="psl-page__inline-email">{{ refundExecItem.learner_email }}</small>
            </strong>
          </div>
          <div class="psl-page__cancel-row">
            <span>교재</span>
            <strong>{{ refundExecItem.textbook_title }}</strong>
          </div>
          <div class="psl-page__cancel-row psl-page__cancel-row--highlight">
            <span>결제금액</span>
            <strong>{{ formatCurrency(refundExecItem.amount, refundExecItem.currency_code) }}</strong>
          </div>
        </div>

        <!-- 환불 단위는 "결제 건" 입니다. 그 결제가 만든 이용권은 영구 도서라도 예외 없이 정지됩니다. -->
        <div class="psl-page__cancel-warn psl-page__cancel-warn--info">
          <i class="material-icons-round">info</i>
          <div>
            이 결제로 지급된 <strong>도서 열람권(영구 포함)과 기능 이용권이 모두 즉시 정지</strong>됩니다.
            전액 환불이면 사용한 쿠폰이 자동 복원됩니다.
          </div>
        </div>

        <!--
          업그레이드(기능) 결제는 도서 결제와 별개 결제라 함께 환불되지 않습니다.
          도서만 환불하면 서버에 고아 feature 경고가 남으므로, 같은 학습자·교재의 미환불 결제를 미리 보여줍니다.
        -->
        <div v-if="relatedPayments.length" class="psl-page__cancel-warn psl-page__cancel-warn--hard">
          <i class="material-icons-round">warning</i>
          <div>
            같은 학습자·교재에 <strong>환불되지 않은 다른 결제 {{ relatedPayments.length }}건</strong>이 있습니다.
            각각 별개 결제라 자동으로 함께 환불되지 않습니다 — 필요하면 따로 환불해 주세요.
            <ul class="psl-page__related">
              <li v-for="p in relatedPayments" :key="p.payment_idx">
                <APill v-if="hasPurchaseType(p)" :kind="purchaseTypePillKind(p.purchase_type)">
                  {{ purchaseTypeLabel(p.purchase_type) }}
                </APill>
                <!--
                  이 목록은 결제 이력과 같은 API 를 다시 부르므로 테스트 결제도 함께 섞여 옵니다.
                  걸러내지 않는 이유: 테스트 결제도 이용권을 만들어 두므로 운영자가 알아야 합니다.
                  대신 표시해 두지 않으면 위 "미환불 N건"이 부풀어 없는 환불 작업을 만듭니다.
                -->
                <APill v-if="isTestRow(p)" kind="dark">테스트 결제</APill>
                <span class="psl-page__mono">{{ formatDate(p.payment_date) }}</span>
                <span class="psl-page__mono">
                  {{ formatCurrency(p.amount, p.currency_code) }}
                </span>
                <span>{{ getPaymentMethodLabel(p.payment_method) }}</span>
                <!-- L 도 2026-08-31 부터 이 화면에서 환불되므로 결제수단이 아니라 실행 가능 여부로 판정합니다 -->
                <small v-if="!canRefundExec(p)">이 화면에서 환불 불가 — 스토어에서 사용자가 직접 환불</small>
                <!-- 환불 대상이긴 하나 기한이 지난 건. "스토어에서 환불" 과는 다른 사유라 문구를 나눕니다 -->
                <small v-else-if="isRefundWindowClosed(p)">{{ refundWindowClosedText(p) }}</small>
              </li>
            </ul>
          </div>
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">notes</i>
            취소 사유 <span class="p-field__req">*</span>
          </label>
          <textarea
            v-model="refundExecForm.cancel_reason"
            class="p-input p-input--area"
            rows="3"
            placeholder="취소 사유를 입력해주세요. (1~200자)"
            maxlength="200"
          ></textarea>
          <small class="psl-page__counter">{{ refundExecForm.cancel_reason.length }}/200</small>
        </div>

        <!--
          부분 취소 금액 입력은 두지 않습니다. 부분 환불도 이용권은 전부 정지되는데 쿠폰은 복원되지 않아
          비대칭이 생기므로 운영 원칙이 전액 환불입니다.

          LS 세전 통지(2026-09-11, docs/환불_즉시반영_관리자화면_가이드.md §3.3)가 이 입력란을 두는 경우의 라벨·최대값을 제안했지만, 같은 문단에서
          백엔드도 운영 원칙은 전액 환불이라고 못박았으므로 입력란 자체를 그대로 두지 않습니다.
          (두게 되면 cancel_amount 는 세전 = 위 "결제금액" 기준이고 최대값이 그 금액입니다.)
        -->
        <!-- 가상계좌는 토스에만 있는 개념입니다. LemonSqueezy 는 원 결제수단으로 되돌아갑니다. -->
        <template v-if="refundExecMethod === 'T'">
          <label class="p-check">
            <input type="checkbox" v-model="refundExecForm.isVirtualAccount" />
            가상계좌 결제 (환불 계좌 입력 필요)
          </label>

          <div v-if="refundExecForm.isVirtualAccount" class="psl-page__bank-info">
            <div class="psl-page__bank-label">
              <i class="material-icons-round">info</i>
              환불 받을 계좌 정보
            </div>
            <div class="p-field">
              <input v-model="refundExecForm.refund_receive_account.bank" type="text" class="p-input" placeholder="은행코드 (예: 004 - 국민은행)" />
            </div>
            <div class="p-field">
              <input v-model="refundExecForm.refund_receive_account.accountNumber" type="text" class="p-input" placeholder="계좌번호" />
            </div>
            <div class="p-field">
              <input v-model="refundExecForm.refund_receive_account.holderName" type="text" class="p-input" placeholder="예금주명" />
            </div>
          </div>
        </template>

        <!--
          LS 는 종전에 운영자가 LemonSqueezy 대시보드로 이동해 환불했습니다.
          이제 여기서 끝나므로, 같은 건을 대시보드에서 또 환불하지 않도록 알려 둡니다.
        -->
        <div v-else-if="refundExecMethod === 'L'" class="psl-page__cancel-warn psl-page__cancel-warn--info">
          <i class="material-icons-round">info</i>
          <div>
            LemonSqueezy 에 환불을 요청한 뒤 <strong>서버 반영까지 한 번에 끝납니다.</strong>
            LS 대시보드에도 같은 환불로 표시되므로 그쪽에서 다시 처리하지 마세요.
            <!--
              LS 는 판매 대행자라 구매자 국가의 세금을 가격 위에 더해 받고, 환불할 때도 세금까지 돌려줍니다.
              우리 기록은 세전(결제 기록 금액) 기준이므로 두 금액이 다를 수 있습니다(docs/환불_즉시반영_관리자화면_가이드.md §3.3).
              CS 에서 "얼마 돌려받았냐"를 이 화면 숫자로 답하면 세금만큼 틀리므로 미리 갈라 둡니다.
            -->
            <span class="psl-page__cancel-note">
              구매자는 결제 때 낸 <strong>세금까지 함께</strong> 돌려받습니다. 이 화면과 통계에 남는 환불 금액은
              세금을 뺀 위 결제금액 기준이라, 실제 환불액은 <strong>LS 대시보드</strong>에서 확인해 주세요.
            </span>
          </div>
        </div>
      </div>

      <template #foot>
        <ABtn :disabled="refundExecLoading" @click="closeRefundExecModal">취소</ABtn>
        <ABtn
          kind="danger-fill" icon="money_off"
          :loading="refundExecLoading"
          :disabled="refundExecLoading || !refundExecForm.cancel_reason.trim() || !!refundWindowBlock"
          @click="submitRefundExec"
        >환불 처리</ABtn>
      </template>
    </AModal>

    <!--
      환불 즉시 반영 결과 (POST /payment/admin/refundSync)

      결과를 토스트 한 줄로 줄이면 "확인 불가"와 "환불 아님"이 같은 말로 읽힙니다.
      조회 실패를 환불 아님으로 뭉개면 운영자가 CS 를 잘못 종결하고 사용자는 환불받은 채로
      계속 열람하게 되므로(가이드 §3.2), 판정 근거까지 펼쳐 보여주는 모달로 둡니다.
    -->
    <AModal v-model="showRefundSyncModal" size="md">
      <template #title>
        <span class="psl-page__modal-title">
          <i class="material-icons-round psl-page__modal-icon">sync</i>
          환불 즉시 반영 결과
        </span>
      </template>
      <template #sub>스토어에 환불 여부를 물어 서버에 반영합니다. 돈이 움직이지는 않습니다.</template>

      <div v-if="refundSyncResult" class="psl-page__cancel">
        <div :class="['psl-page__sync-head', `is-${refundSyncOutcome}`]">
          <i class="material-icons-round">{{ refundSyncMeta.icon }}</i>
          <div>
            <strong>{{ refundSyncMeta.label }}</strong>
            <!-- detail 은 서버가 사람이 읽으라고 내려주는 문구라 그대로 노출합니다(가이드 §3.2). -->
            <p>{{ refundSyncResult.detail || "서버가 결과 문구를 보내지 않았습니다." }}</p>
          </div>
        </div>

        <!-- 이 화면에서 가장 중요한 안내입니다. 확인 불가를 환불 아님으로 읽으면 CS 오종결로 이어집니다. -->
        <div v-if="refundSyncOutcome === 'unknown'" class="psl-page__cancel-warn">
          <i class="material-icons-round">help_outline</i>
          <div>
            <strong>"환불 아님"이 아니라 "확인할 수 없음"입니다.</strong>
            스토어 자격증명 누락 · 스토어 API 장애 · 결제일이 조회 기간 밖 중 하나입니다.
            스토어 콘솔에서 직접 확인한 뒤에 CS 를 종결해 주세요.
          </div>
        </div>

        <!-- 반영 자체는 이미 됐습니다. 금액이 맞는지는 사람이 봐야 합니다. -->
        <div v-if="refundSyncResult.partial_refund_suspected" class="psl-page__cancel-warn">
          <i class="material-icons-round">warning</i>
          <div>
            <strong>부분 환불로 보입니다.</strong>
            환불 금액이 결제 금액보다 적습니다 — 반영은 마쳤으니 금액을 확인해 주세요.
          </div>
        </div>

        <!-- 샌드박스 결제는 프로덕션 스토어에 없어 "목록에 없음"이 곧 "환불 아님"이 아닙니다. -->
        <div v-if="isTestRow(refundSyncResult)" class="psl-page__cancel-warn psl-page__cancel-warn--info">
          <i class="material-icons-round">science</i>
          <div>
            <strong>테스트(샌드박스) 결제입니다.</strong>
            프로덕션 스토어에는 존재하지 않으므로 조회 결과를 판정 근거로 삼지 마세요.
          </div>
        </div>

        <div class="psl-page__cancel-summary">
          <div class="psl-page__cancel-row">
            <span>결제</span>
            <strong>
              #{{ refundSyncResult.payment_idx }} · {{ getPaymentMethodLabel(refundSyncResult.payment_method) }}
            </strong>
          </div>
          <div class="psl-page__cancel-row">
            <span>스토어 조회</span>
            <strong>
              {{ refundSyncResult.store_lookup_ok ? "성공" : "실패" }}
              · {{ lookupSourceLabel(refundSyncResult.lookup_source) }}
            </strong>
          </div>
          <div class="psl-page__cancel-row">
            <span>결제 코드</span>
            <strong class="psl-page__mono">{{ refundSyncResult.pay_code || "-" }}</strong>
          </div>
          <!-- 코드가 정확히 일치하지 않으면 스토어에서 다른 항목을 본 것일 수 있습니다 -->
          <div v-if="refundSyncResult.matched_exact === false" class="psl-page__cancel-row">
            <span>코드 일치</span>
            <strong>부분 일치 — 조회 결과를 한 번 더 확인해 주세요</strong>
          </div>
          <div v-if="refundSyncResult.refund_at" class="psl-page__cancel-row psl-page__cancel-row--highlight">
            <span>환불 반영</span>
            <strong>
              {{ formatDateTime(refundSyncResult.refund_at) }}
              <template v-if="refundSyncResult.refund_amount != null">
                · {{ formatCurrency(refundSyncResult.refund_amount, refundSyncResult.refund_currency_code || refundSyncItem?.currency_code) }}
              </template>
            </strong>
          </div>
        </div>

        <!--
          force 재반영. 쓸 일은 하나뿐입니다 — 환불 기록은 남았는데 이용권이 회수되지 않은
          옛 데이터의 복구(두 작업이 별개 트랜잭션이던 시절의 잔재). 누르면 학습자에게 환불
          푸시가 다시 나가므로 기본 버튼과 분리하고 확인 대화상자를 겁니다(가이드 §3.2 force).
        -->
        <details v-if="refundSyncOutcome === 'already'" class="psl-page__advanced">
          <summary>고급 — 이용권이 회수되지 않았다면</summary>
          <p>
            환불 기록은 있는데 학습자가 여전히 교재를 볼 수 있는 경우에만 씁니다.
            <strong>학습자에게 환불 푸시가 다시 발송됩니다.</strong>
          </p>
          <ABtn
            kind="danger" icon="restart_alt"
            :loading="refundSyncLoading"
            :disabled="refundSyncLoading"
            @click="runRefundSyncForce"
          >강제 재반영</ABtn>
        </details>
      </div>

      <template #foot>
        <ABtn @click="closeRefundSyncModal">닫기</ABtn>
      </template>
    </AModal>

    <!-- PG 수수료 정보 모달 -->
    <AModal v-model="showPgFeeModal" size="md">
      <template #title>
        <span class="psl-page__modal-title">
          <i class="material-icons-round psl-page__modal-icon">payments</i>
          PG 수수료 정보
        </span>
      </template>

      <div v-if="pgFeeLoading" class="psl-page__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>수수료 정보를 불러오는 중...</p>
      </div>

      <div v-else-if="pgFeeInfo" class="psl-page__fees">
        <div v-for="(fee, pg) in pgFeeInfo" :key="pg" class="psl-page__fee-item">
          <div class="psl-page__fee-pg">
            <div :class="['psl-page__fee-icon', `psl-page__fee-icon--${pg}`]">
              <img v-if="pg === 'toss'" :src="tossLogo" alt="Toss" />
              <i v-else class="material-icons-round">{{ getPgIcon(pg) }}</i>
            </div>
            <span class="psl-page__fee-name">{{ getPgDisplayName(pg) }}</span>
          </div>
          <span class="psl-page__fee-rate">{{ formatFeePercent(fee) }}</span>
        </div>
      </div>

      <AEmpty v-else title="수수료 정보를 불러올 수 없어요" size="sm" />

      <template #foot>
        <ABtn @click="closePgFeeModal">닫기</ABtn>
      </template>
    </AModal>

    <!-- 무료/사은품 등록 모달 -->
    <AModal v-model="showFreeItemModal" size="lg" :close-on-backdrop="!freeItemLoading">
      <template #title>
        <span class="psl-page__modal-title">
          <i class="material-icons-round psl-page__modal-icon">card_giftcard</i>
          무료/사은품 등록
        </span>
      </template>
      <template #sub>사용자에게 도서를 무료로 증정합니다. 도서 열람권은 영구로 지급됩니다.</template>

      <div class="psl-page__free">
        <!-- ISBN -->
        <section class="psl-page__free-section">
          <header class="psl-page__free-head">
            <div class="psl-page__free-icon psl-page__free-icon--info">
              <i class="material-icons-round">menu_book</i>
            </div>
            <div class="psl-page__free-head-text">
              <label>ISBN <span class="p-field__req">*</span></label>
              <small>증정할 도서의 ISBN을 입력하세요.</small>
            </div>
            <APill kind="default">{{ freeItemForm.isbns.length }}건</APill>
          </header>

          <div
            v-for="(_, index) in freeItemForm.isbns"
            :key="`isbn-${index}`"
            class="psl-page__dynamic-row"
          >
            <span class="psl-page__row-idx">{{ index + 1 }}</span>
            <input
              v-model="freeItemForm.isbns[index]"
              type="text"
              class="p-input"
              placeholder="예: 9788952131195"
            />
            <button
              class="p-icon-btn p-icon-btn--danger"
              type="button"
              v-tooltip
              title="ISBN 행 삭제"
              :disabled="freeItemForm.isbns.length <= 1"
              @click="removeIsbnRow(index)"
            ><i class="material-icons-round">remove</i></button>
          </div>
          <ABtn size="sm" icon="add" @click="addIsbnRow">ISBN 추가</ABtn>
        </section>

        <!-- Email -->
        <section class="psl-page__free-section">
          <header class="psl-page__free-head">
            <div class="psl-page__free-icon psl-page__free-icon--ok">
              <i class="material-icons-round">mail</i>
            </div>
            <div class="psl-page__free-head-text">
              <label>사용자 이메일 <span class="p-field__req">*</span></label>
              <small>증정 받을 사용자의 이메일 주소</small>
            </div>
            <APill kind="default">{{ freeItemForm.emails.length }}건</APill>
          </header>

          <div
            v-for="(_, index) in freeItemForm.emails"
            :key="`email-${index}`"
            class="psl-page__dynamic-row"
          >
            <span class="psl-page__row-idx">{{ index + 1 }}</span>
            <input
              v-model="freeItemForm.emails[index]"
              type="email"
              class="p-input"
              placeholder="예: user@example.com"
            />
            <button
              class="p-icon-btn p-icon-btn--danger"
              type="button"
              v-tooltip
              title="이메일 행 삭제"
              :disabled="freeItemForm.emails.length <= 1"
              @click="removeEmailRow(index)"
            ><i class="material-icons-round">remove</i></button>
          </div>
          <ABtn size="sm" icon="add" @click="addEmailRow">이메일 추가</ABtn>
        </section>

        <!-- 지급 등급 -->
        <section class="psl-page__free-section">
          <header class="psl-page__free-head">
            <div class="psl-page__free-icon psl-page__free-icon--point">
              <i class="material-icons-round">workspace_premium</i>
            </div>
            <div class="psl-page__free-head-text">
              <label>지급 등급 <span class="p-field__req">*</span></label>
              <small>
                지급 내용은 상품의 <strong>등급</strong>으로만 결정됩니다.
                무료 지급이라 도서 타입·스토어·금액은 결과에 영향을 주지 않습니다.
              </small>
            </div>
          </header>

          <div v-if="pricingLoading" class="psl-page__free-grade-loading">
            <div class="p-spinner"></div>
            상품 목록을 불러오는 중...
          </div>

          <template v-else-if="canPickGrade">
            <div class="psl-page__grade-picker">
              <button
                v-for="opt in freeGradeOptions"
                :key="opt.value"
                type="button"
                :class="['psl-page__grade-card', { 'is-active': freeItemForm.grade === opt.value }]"
                @click="freeItemForm.grade = opt.value"
              >
                <strong>{{ opt.label }}</strong>
                <small>{{ opt.grants }}</small>
              </button>
            </div>
            <div class="psl-page__free-fixed-note">
              <i class="material-icons-round">info</i>
              <span>
                전송값 <strong>product_pricing_idx = {{ resolvedPricingIdx }}</strong>
                <template v-if="selectedPricing"> · {{ selectedPricing.product_description }}</template>
              </span>
            </div>
          </template>

          <!--
            개편 배포 전 응답에는 상품 PK 가 없습니다. 그때는 등급을 고를 수 없으므로
            종전 고정값으로 보내고, 왜 못 고르는지 화면에 밝힙니다.
          -->
          <div v-else class="psl-page__free-fixed-note psl-page__free-fixed-note--warn">
            <i class="material-icons-round">warning</i>
            <span>
              가격표 API 가 아직 상품 PK(product_pricing_idx)를 내려주지 않아 등급을 고를 수 없습니다.
              종전 고정값 <strong>{{ resolvedPricingIdx }}</strong> 으로 전송됩니다 —
              백엔드 개편이 배포되면 선택 UI 가 자동으로 열립니다.
            </span>
          </div>
        </section>

        <!-- Plan -->
        <section class="psl-page__free-section" :class="{ 'is-dimmed': !featurePeriodApplies }">
          <header class="psl-page__free-head">
            <div class="psl-page__free-icon psl-page__free-icon--warn">
              <i class="material-icons-round">event</i>
            </div>
            <div class="psl-page__free-head-text">
              <label>기능 이용 기간(개월) <span class="p-field__req">*</span></label>
              <small v-if="featurePeriodApplies">
                도서 열람권은 항상 영구로 지급되고, 이 값은 기능 이용권에만 적용됩니다. (기본 6개월)
              </small>
              <small v-else>
                <strong>Basic 은 기능 이용권이 없어 이 값이 무시됩니다.</strong>
                도서 열람권은 영구로 지급됩니다.
              </small>
            </div>
          </header>
          <div class="psl-page__plan-input">
            <input
              v-model.number="freeItemForm.plan_months"
              type="number"
              class="p-input"
              placeholder="6"
              min="1"
            />
            <span class="psl-page__plan-unit">개월</span>
          </div>
          <div class="psl-page__plan-quick">
            <button
              v-for="m in [1, 3, 6, 12]"
              :key="m"
              type="button"
              :class="['psl-page__plan-chip', { 'is-active': freeItemForm.plan_months === m }]"
              @click="freeItemForm.plan_months = m"
            >{{ m }}개월</button>
          </div>
        </section>

        <!-- 자동 전송 값 -->
        <section class="psl-page__free-fixed">
          <header class="psl-page__free-fixed-head">
            <i class="material-icons-round">bolt</i>
            <span>자동 전송 값</span>
            <small>(선택한 상품에 따라 정해집니다)</small>
          </header>
          <div class="psl-page__free-fixed-grid">
            <div class="psl-page__fixed-row"><span>pay_code</span><strong>{{ freeItemForm.pay_code }}</strong></div>
            <div class="psl-page__fixed-row"><span>amount</span><strong>{{ freeItemForm.amount }}</strong></div>
            <div class="psl-page__fixed-row"><span>coupon_amount</span><strong>{{ freeItemForm.coupon_amount }}</strong></div>
            <div class="psl-page__fixed-row"><span>currency_code</span><strong>{{ resolvedCurrencyCode }}</strong></div>
            <div class="psl-page__fixed-row"><span>payment_method</span><strong>{{ freeItemForm.payment_method }} (무료/사은품)</strong></div>
          </div>
          <div class="psl-page__free-fixed-note">
            <i class="material-icons-round">info</i>
            <span>
              무료 사은품은 쿠폰을 사용한 결제가 아니므로 <strong>coupon_amount 는 0</strong> 으로 보냅니다.
              통화는 선택한 상품을 따릅니다.
            </span>
          </div>
        </section>

        <!--
          ISBN × 이메일 조합마다 결과가 다릅니다(성공 / 이미 보유해서 skip / 대상 없음).
          종전에는 응답을 버리고 "등록 완료" 만 띄워, 전 행이 skip 돼도 성공처럼 보였습니다.
        -->
        <section v-if="freeItemResults.length" class="psl-page__free-result">
          <header class="psl-page__free-result-head">
            <i class="material-icons-round">fact_check</i>
            <span>등록 결과</span>
            <APill v-if="freeResultSummary.ok" kind="ok">성공 {{ freeResultSummary.ok }}</APill>
            <APill v-if="freeResultSummary.warn" kind="warn">건너뜀 {{ freeResultSummary.warn }}</APill>
            <APill v-if="freeResultSummary.err" kind="err">실패 {{ freeResultSummary.err }}</APill>
          </header>
          <div
            v-for="(r, i) in freeItemResults"
            :key="`free-result-${i}`"
            :class="['psl-page__free-result-row', `is-${freeResultKind(r)}`]"
          >
            <i class="material-icons-round">{{ freeResultIcon(r) }}</i>
            <div class="psl-page__free-result-text">
              <strong>{{ r.user_email || '-' }}</strong>
              <small>{{ r.textbook_title || '-' }}</small>
              <!-- 서버 문구를 그대로 노출합니다. 화면에서 문구로 분기하지 않습니다. -->
              <span>{{ r.message || '결과 문구가 없습니다.' }}</span>
            </div>
            <code v-if="r.payment_id" class="psl-page__free-result-id">#{{ r.payment_id }}</code>
          </div>
        </section>
      </div>

      <template #foot>
        <ABtn v-if="freeItemResults.length" icon="refresh" @click="resetFreeItemResults">새로 등록</ABtn>
        <ABtn @click="closeFreeItemModal">닫기</ABtn>
        <ABtn
          kind="primary" icon="card_giftcard"
          :loading="freeItemLoading"
          :disabled="freeItemLoading || !canSubmitFreeItem"
          @click="submitFreeItem"
        >등록하기</ABtn>
      </template>
    </AModal>

    <!-- 무료/사은품 삭제 확인 -->
    <AModal v-model="showFreeDeleteModal" size="sm" :close-on-backdrop="!freeDeleteLoading">
      <template #title>
        <span class="psl-page__modal-title">
          <i class="material-icons-round psl-page__modal-icon psl-page__modal-icon--danger">delete_forever</i>
          무료/사은품 삭제
        </span>
      </template>
      <template #sub>잘못 등록된 무료/사은품 기록을 완전히 지웁니다.</template>

      <div v-if="freeDeleteItem" class="psl-page__cancel">
        <div class="psl-page__cancel-summary">
          <div class="psl-page__cancel-row">
            <span>학습자</span>
            <strong>
              {{ freeDeleteItem.learner_name }}
              <small v-if="freeDeleteItem.learner_email" class="psl-page__inline-email">{{ freeDeleteItem.learner_email }}</small>
            </strong>
          </div>
          <div class="psl-page__cancel-row">
            <span>교재</span>
            <strong>{{ freeDeleteItem.textbook_title }}</strong>
          </div>
          <div class="psl-page__cancel-row">
            <span>이용 기간</span>
            <strong>
              <template v-if="hasNoPlan(freeDeleteItem)">—</template>
              <template v-else>
                {{ formatDate(freeDeleteItem.start_date) }} ~ {{ endDateText(freeDeleteItem) }}
                <!-- std/prem 지급 건은 도서 + 기능 2개가 함께 지워지므로 기능 만료일도 보여 줍니다 -->
                <template v-if="featurePeriodNote(freeDeleteItem)"> · {{ featurePeriodNote(freeDeleteItem) }}</template>
              </template>
            </strong>
          </div>
          <div class="psl-page__cancel-row psl-page__cancel-row--highlight">
            <span>상태</span>
            <strong>
              {{ isRefunded(freeDeleteItem) ? '환불' : getRentalStatusLabel(freeDeleteItem.rental_status) }}
            </strong>
          </div>
        </div>

        <!-- 지금 쓰고 있는 이용권을 끊는 것이므로 따로 경고합니다 -->
        <div v-if="freeDeleteItem.rental_status === 'ACTIVE'" class="psl-page__cancel-warn">
          <i class="material-icons-round">warning</i>
          <div>
            <strong>현재 활성 이용권입니다.</strong>
            삭제하면 학습자가 이 교재를 즉시 볼 수 없게 됩니다.
            <template v-if="isPermanentRow(freeDeleteItem)">영구로 지급된 도서 열람권도 함께 사라집니다.</template>
          </div>
        </div>

        <div class="psl-page__cancel-warn psl-page__cancel-warn--hard">
          <i class="material-icons-round">delete_forever</i>
          <div>결제 기록 · 영수증 · 구독이 <strong>모두 삭제되며 복구할 수 없습니다.</strong></div>
        </div>
      </div>

      <template #foot>
        <ABtn :disabled="freeDeleteLoading" @click="closeFreeDeleteModal">취소</ABtn>
        <ABtn
          kind="danger-fill" icon="delete_forever"
          :loading="freeDeleteLoading"
          :disabled="freeDeleteLoading"
          @click="submitFreeDelete"
        >{{ freeDeleteLoading ? '삭제 중' : '삭제' }}</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import SearchArea from "@/components/SearchArea.vue";
import InvoiceDetailModal from "./components/InvoiceDetailModal.vue";
import * as XLSX from "xlsx";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import { apiErrorMessage, apiErrorStatus } from "@/utils/apiError";
import * as pay from "@/utils/paymentDisplay";
import { formatMoney } from "@/utils/currency";

// 가격표 API 가 상품 PK 를 내려주기 전(개편 배포 전)에 쓰던 값입니다.
// 그때는 화면에서 등급을 고를 수 없어 이 값 하나로만 지급했습니다.
const FALLBACK_PRICING_IDX = 3;

// POST /lemonsqueezy/cancel 이 200 으로 돌려주는 reason 전체입니다(백엔드 회신 2026-08-31).
//   applied         이번 호출로 환불 + 서버 반영까지 끝냄
//   already_applied 웹훅이 먼저 반영함(경쟁 상황) — 역시 완료 상태
// 둘 다 완료라 화면 문구가 같습니다. 목록에 두는 이유는 "모르는 reason"을 가려내기 위함입니다.
//
// 서비스에는 payment_not_found 가 하나 더 있습니다. 라우트가 payment_idx 로 그 행을 이미
// 로드한 뒤 호출하므로 동시 삭제 같은 이상 상황에서만 나오고, 그때는 "환불은 됐는데 반영 대상을
// 못 찾은" 상태라 완료로 처리하면 안 됩니다 — 일부러 화이트리스트에 넣지 않아 경고로 떨어집니다
// (백엔드와 합의). 값이 늘어나면 백엔드가 먼저 알려주기로 했습니다.
const LS_CANCEL_REASONS = ["applied", "already_applied"];

let tossLogo;
try { tossLogo = require("@/assets/img/toss.png"); } catch (e) { tossLogo = ""; }

export default {
  name: "PaymentSalesList",
  components: { SearchArea, InvoiceDetailModal, APageHead, ABtn, ACard, AStat, APill, ATable, AEmpty, APagination, AModal },
  data() {
    return {
      tossLogo,
      // 뱃지·툴팁 문구. 인보이스 모달도 같은 문장을 써야 해서 유틸 상수를 그대로 끌어옵니다.
      SUPERSEDED_TEXT: pay.SUPERSEDED_TEXT,
      SUPERSEDED_HINT: pay.SUPERSEDED_HINT,
      CREDITED_HINT: pay.CREDITED_HINT,
      FEATURE_ENDED_TEXT: pay.FEATURE_ENDED_TEXT,
      FEATURE_ENDED_HINT: pay.FEATURE_ENDED_HINT,
      REFUND_WINDOW_DAYS: pay.REFUND_WINDOW_DAYS,
      // LS 환불 금액이 세전 기록이라는 안내(docs/환불_즉시반영_관리자화면_가이드.md §3.3). 목록·모달이 같은 문장을 씁니다.
      LS_PRETAX_HINT:
        "LemonSqueezy 는 구매자 국가의 세금을 더해 청구하고 환불 때 함께 돌려줍니다. " +
        "여기 금액은 세금을 뺀 결제 기록 금액 기준이라, 구매자가 실제로 돌려받은 금액은 LS 대시보드에서 확인해 주세요.",
      tableData: [],
      totalCount: 0,
      // 목록 / 달력 보기. 검색 조건·탭은 공유하고 데이터 요청만 다릅니다.
      viewMode: "list",
      viewModes: [
        { value: "list", label: "목록", icon: "view_list" },
        { value: "calendar", label: "달력", icon: "calendar_month" },
      ],
      calYear: new Date().getFullYear(),
      calMonth: new Date().getMonth() + 1, // 1~12
      calDow: ["일", "월", "화", "수", "목", "금", "토"],
      // { "YYYY-MM-DD": { count, refunds, amounts: [{ currency_code, amount, refund, net }] } }
      // amount 는 결제금액, refund 는 그중 환불된 금액, net = amount − refund (같은 결제 통화 기준).
      calDays: {},
      calCount: 0,
      calTotals: [],
      calLoading: false,
      // 조회 조건 전체의 통화별 합계 [{ currency_code, count, amount, refund_amount }].
      // 미배포 서버는 필드가 없어 [] 로 남고, 화면은 합계 줄을 숨깁니다.
      amountTotals: [],
      currentPage: 1,
      itemsPerPage: 10,
      searchValues: {},
      activeTab: "all",
      excludeFree: false,
      // 순액 병기의 툴팁. "이번 달 환불 발생액"(환불일 기준, 현금흐름)이 아니라는 점을 분명히 합니다 —
      // 백엔드 실측(2026-09-08)에서 두 기준의 차이가 컸습니다(9월: 358,140 vs 493,840 — docs/결제_영구소유_개편_관리자화면_가이드.md §3.3).
      REVENUE_NET_TIP: "순액 = 매출 − 환불. 결제일 기준이라 이번 달에 판 것 중 환불된 금액만 뺍니다. 지난달 결제분의 환불은 지난달에서 빠집니다.",
      stats: {
        // 응답 도착 전 로딩 표시용 자리값입니다. 실제 통화는 응답의 currency 를 씁니다.
        totalRevenue: { amount: 0, currency: "KRW" },
        monthRevenue: { amount: 0, currency: "KRW" },
        activeSubscriptions: { count: 0 },
        totalTransactions: { count: 0 },
      },
      selectedItems: [],
      showInvoiceModal: false,
      selectedPayment: null,
      invoiceDetails: null,
      showPgFeeModal: false,
      pgFeeInfo: null,
      pgFeeLoading: false,
      // 환불 실행(T/L) — 결제사에 취소를 거는 모달. 조회만 하는 refundSync 와 별개입니다.
      showRefundExecModal: false,
      refundExecItem: null,
      refundExecLoading: false,
      refundExecForm: {
        cancel_reason: "",
        isVirtualAccount: false,
        refund_receive_account: { bank: "", accountNumber: "", holderName: "" },
      },
      // 같은 학습자·교재의 미환불 결제(동반 환불 판단용)
      relatedPayments: [],
      // 환불 순서 가드 409 (정책 §6-3). { detail, payCode } — null 이면 안내를 그리지 않습니다.
      refundOrderGuard: null,
      // 환불 즉시 반영(A/G/T) — 행 단위로 도는 작업이라 어느 행이 도는지 payment_idx 로 잡습니다.
      // 전역 boolean 으로 두면 여러 행의 버튼이 한꺼번에 잠깁니다.
      refundSyncBusyIdx: null,
      showRefundSyncModal: false,
      refundSyncItem: null,
      refundSyncResult: null,
      refundSyncLoading: false,
      showFreeItemModal: false,
      freeItemLoading: false,
      // 응답 data[] — ISBN × 이메일 조합마다 1행. 행별 message 를 그대로 보여줍니다.
      freeItemResults: [],
      // 무료 지급용 상품 목록(product_kind='total' 행). 모달을 처음 열 때 한 번만 받습니다.
      pricingOptions: [],
      pricingLoading: false,
      freeItemForm: {
        isbns: [""],
        emails: [""],
        plan_months: 6,
        grade: "basic",
        pay_code: "AUTO_GENERATED",
        amount: 0,
        // 무료 사은품은 쿠폰을 쓴 결제가 아니라 0 입니다.
        // 종전 기본값 19.99 는 "12개월 대여 $19.99" 시절의 흔적이라 어떤 상품가와도 맞지 않았습니다.
        coupon_amount: 0,
        payment_method: "F",
      },
      showFreeDeleteModal: false,
      freeDeleteItem: null,
      freeDeleteLoading: false,
      tabs: [
        {
          value: "all", label: "전체", icon: "list", count: 0,
          // 탭 숫자가 배포 직후 이유 없이 늘어난 것처럼 보입니다. 그 자리에서 사유를 알립니다.
          hint: "테스트 결제를 포함한 건수입니다. 매출 KPI(총 매출·이번 달 매출)는 테스트 결제를 제외합니다.",
        },
        { value: "ACTIVE", label: "활성", icon: "check_circle", count: 0 },
        {
          value: "EXPIRED", label: "만료", icon: "event_busy", count: 0,
          // 도서가 영구 소유로 바뀌면서 자연만료가 사라집니다. 운영이 "왜 갑자기 줄었지"를 그 자리에서 알 수 있게 합니다.
          // 여기에 "상위 등급 전환으로 종료된 기능 이용권"이 더해집니다(정책 §5). 그 행은
          // 종료일이 미래인 채로 만료에 잡혀서, 적어 두지 않으면 필터 오작동으로 읽힙니다.
          hint:
            "도서 영구 소유 전환 이후 환불 건 · 무료 지급 만료 건 · 수동 만료 건 · " +
            "상위 등급 전환으로 종료된 기능 이용권('전환 종료')이 남습니다.",
        },
        { value: "SCHEDULED", label: "예정", icon: "schedule", count: 0 },
      ],
      searchFields: [
        // 파라미터 이름은 그대로지만 서버가 이름 OR 이메일로 찾습니다.
        { key: "learner_name", placeholder: "학습자명 또는 이메일", col: "col-md-2" },
        { key: "textbook_title", placeholder: "교재명", col: "col-md-2" },
        {
          key: "payment_method", placeholder: "결제수단", type: "multiselect", col: "col-md-2",
          options: [
            // Stripe(S) 는 도입을 검토만 하고 실제 결제가 한 건도 없습니다. 골라도 늘 0건이라 뺍니다.
            { value: "A", label: "Apple Pay" },
            { value: "G", label: "Google Pay" },
            { value: "T", label: "Toss" },
            { value: "F", label: "무료/사은품" },
            { value: "L", label: "Lemonsqueezy" },
            // 기관 바우처(docs/바우처_기관발행_관리자화면_가이드.md §5). 학생이 아니라 기관 잔액에서 차감된 결제입니다.
            { value: "V", label: "기관 바우처" },
          ],
        },
        {
          key: "rental_status", placeholder: "이용 상태", type: "select", col: "col-md-2",
          options: [
            { value: "", label: "전체" },
            { value: "ACTIVE", label: "활성" },
            { value: "EXPIRED", label: "만료" },
            { value: "SCHEDULED", label: "예정" },
          ],
        },
        {
          // 서버 파라미터: 생략=전체 / false=제외 / true=테스트만.
          // 서버가 total_count 도 같은 조건으로 계산하므로 페이지네이션·탭 숫자가 맞습니다.
          // 클라이언트에서 거르면 현재 페이지 10건 안에서만 걸러져 전부 어긋납니다.
          key: "is_test", placeholder: "테스트 결제", type: "select", col: "col-md-2",
          options: [
            { value: "", label: "전체" },
            { value: "false", label: "테스트 제외" },
            { value: "true", label: "테스트만" },
          ],
        },
        {
          // 서버 파라미터: 생략=전체 / NONE=미환불 / REFUNDED=환불됨.
          // 이 필터가 없으면 "환불 즉시 반영" 버튼을 누를 행을 목록에서 찾을 수단이 없습니다.
          // 잘못된 값은 서버가 400 으로 명시적으로 거부합니다(조용히 무필터가 되지 않도록).
          key: "refund_status", placeholder: "환불 여부", type: "select", col: "col-md-2",
          options: [
            { value: "", label: "전체" },
            { value: "NONE", label: "미환불" },
            { value: "REFUNDED", label: "환불됨" },
          ],
        },
        { key: "period", placeholder: "결제기간", type: "period", col: "col-md-3" },
      ],
      columns: [
        { key: "select", label: "", align: "center", width: 40 },
        { key: "no", label: "번호", align: "center", width: 60 },
        { key: "learner", label: "학습자", width: 190 },
        { key: "textbook", label: "교재", wrap: true },
        // purchase_type 과 grade 를 한 컬럼에 pill 두 개로 쌓습니다.
        // 따로 두 컬럼을 만들면 11열이 되어 가로 스크롤이 상시화됩니다.
        { key: "product", label: "상품 구분", width: 140 },
        { key: "amount", label: "결제금액", align: "right", width: 120 },
        { key: "method", label: "결제수단", width: 130 },
        { key: "date", label: "결제일", width: 150 },
        { key: "period", label: "이용 기간", width: 170 },
        // 환불 건은 pill 아래에 환불일·환불금액이 붙습니다. 100px 로는 날짜가 두 줄로 접힙니다.
        { key: "status", label: "상태", width: 150 },
        // T 행은 인보이스·PDF·환불 실행·환불 반영 4개가 붙어 130px 를 넘칩니다.
        { key: "actions", label: "관리", align: "right", width: 170 },
      ],
    };
  },
  computed: {
    /**
     * '무료/사은품 제외' 토글이 선택하는 결제수단 목록.
     *
     * 결제수단 필터 옵션에서 F 만 뺀 값입니다. 종전에는 별도 배열로 적어 두어서
     * 결제수단이 늘어도 그 배열은 그대로 남았고, 그 결과 Lemonsqueezy(L) 는 체크되지 않고
     * 쓰지 않는 코드(P)가 대신 들어가 있었습니다. 옵션 한 곳만 고치면 되도록 파생시킵니다.
     */
    nonFreeMethods() {
      const field = this.searchFields.find((f) => f.key === "payment_method");
      return (field?.options || []).map((o) => o.value).filter((v) => v !== "F");
    },
    getItemNumber() {
      return (index) => this.totalCount - ((this.currentPage - 1) * this.itemsPerPage + index);
    },
    // 백엔드 개편 배포 전에는 키 자체가 없습니다. 0 과 "아직 없음"을 구분해야 합니다.
    hasActiveFeatures() {
      return typeof this.stats.activeFeatures === "number";
    },
    canSubmitFreeItem() {
      const hasIsbn = this.freeItemForm.isbns.some((v) => v && v.trim());
      const hasEmail = this.freeItemForm.emails.some((v) => v && v.trim());
      const hasPlan = this.freeItemForm.plan_months >= 1;
      return hasIsbn && hasEmail && hasPlan;
    },
    // 같은 등급이면 어느 행을 써도 지급 내용이 같습니다(도서 타입·스토어는 금액에만 영향).
    // 그래서 등급마다 가장 작은 PK 하나를 대표로 고정합니다 — 응답 순서가 바뀌어도 값이 흔들리지 않습니다.
    gradePricingMap() {
      const map = {};
      this.pricingOptions.forEach((row) => {
        const g = row.grade;
        if (!g) return;
        if (!map[g] || row.product_pricing_idx < map[g].product_pricing_idx) map[g] = row;
      });
      return map;
    },
    freeGradeOptions() {
      const grants = {
        basic: "도서 열람권(영구) 1건",
        standard: "도서 열람권(영구) + Standard 기능 이용권",
        premium: "도서 열람권(영구) + Premium 기능 이용권",
      };
      return ["basic", "standard", "premium"]
        .filter((g) => this.gradePricingMap[g])
        .map((g) => ({ value: g, label: pay.gradeLabel(g), grants: grants[g] }));
    },
    canPickGrade() {
      return this.freeGradeOptions.length > 0;
    },
    selectedPricing() {
      return this.gradePricingMap[this.freeItemForm.grade] || null;
    },
    // 등급을 고를 수 없는 동안(개편 배포 전)에는 종전 고정값을 그대로 보냅니다.
    resolvedPricingIdx() {
      return this.selectedPricing?.product_pricing_idx ?? FALLBACK_PRICING_IDX;
    },
    // product_pricing_idx 가 이미 통화를 결정합니다. 화면이 따로 하드코딩하면
    // KRW 상품을 골라도 USD 가 나가는 모순이 생깁니다.
    resolvedCurrencyCode() {
      return this.selectedPricing?.currency_code || "USD";
    },
    // basic 은 기능 이용권이 없어 plan_months 가 무시됩니다.
    // 등급을 고를 수 없는 동안에는 어떤 상품인지 알 수 없으므로 기간을 계속 받습니다.
    featurePeriodApplies() {
      if (!this.canPickGrade) return true;
      return this.freeItemForm.grade !== "basic";
    },
    freeResultSummary() {
      const sum = { ok: 0, warn: 0, err: 0 };
      this.freeItemResults.forEach((r) => { sum[this.freeResultKind(r)] += 1; });
      return sum;
    },
    // ── 환불 ──
    refundExecMethod() {
      return this.refundExecItem?.payment_method || "";
    },
    refundExecMethodLabel() {
      return this.getPaymentMethodLabel(this.refundExecMethod);
    },
    /**
     * 환불 기한 경과 안내. 결제일·기한을 같이 실어 운영자가 CS 에 그대로 옮겨 적게 합니다.
     * 서버 409 의 detail 도 같은 두 날짜를 담고 있어 화면과 서버 안내가 같은 모양이 됩니다.
     */
    refundWindowBlock() {
      if (!pay.isRefundWindowClosed(this.refundExecItem)) return null;
      return {
        paymentDate: String(this.refundExecItem.payment_date).slice(0, 10),
        deadline: pay.refundDeadlineKey(this.refundExecItem),
      };
    },
    /**
     * 환불 순서 안내를 그릴지, 무엇으로 그릴지.
     *
     * 근거가 둘입니다.
     *   confirmed  서버가 실제로 409 로 막았다 — 최종 판정. detail 원문을 그대로 씁니다.
     *   미확정      모달을 여는 시점에 이 행이 이미 `is_superseded` 였다 (2차답변 §5)
     *
     * 후자를 넣은 이유: 종전에는 운영자가 취소 사유를 다 적고 "되돌릴 수 없습니다" 확인창까지
     * 지난 **뒤에야** 막혔다는 걸 알았습니다. 서버가 필드를 주기로 해서, 이제 그 앞에서 알립니다.
     * 서버 판정이 언제나 우선입니다 — 409 를 받았으면 그쪽을 씁니다.
     */
    refundOrderBlock() {
      if (this.refundOrderGuard) return { ...this.refundOrderGuard, confirmed: true };
      if (!pay.isSupersededRow(this.refundExecItem)) return null;
      return { detail: "", payCode: pay.supersededByPayCode(this.refundExecItem), confirmed: false };
    },
    /**
     * 순서 안내가 지목한 "먼저 환불할 상위 결제" 행.
     *
     * 이 화면에는 결제 코드로 찾는 검색 필드가 없어서, 안내에 pay_code 만 찍어 두면
     * 운영자가 그 결제를 눈으로 뒤져야 합니다. 상위 결제는 정의상 같은 학습자·교재의
     * 미환불 결제라 이미 relatedPayments 안에 들어와 있으므로 거기서 집어 바로 넘겨 줍니다.
     * canRefundExec 로 좁히는 이유: 이 화면에서 환불할 수 없는 결제(A/G 등)로 넘기면
     * 버튼이 아무것도 못 하는 모달을 열게 됩니다.
     */
    supersedingPayment() {
      const code = this.refundOrderBlock?.payCode;
      if (!code) return null;
      return this.relatedPayments.find((p) => p.pay_code === code && pay.canRefundExec(p)) || null;
    },
    refundSyncOutcome() {
      return pay.refundSyncOutcome(this.refundSyncResult);
    },
    refundSyncMeta() {
      return pay.refundSyncOutcomeMeta(this.refundSyncOutcome);
    },
    /**
     * refund_status 필터를 걸었는데 응답이 그 조건을 지키지 않은 상태.
     *
     * 백엔드 미배포 서버는 모르는 쿼리 파라미터를 그냥 무시하므로 요청은 200 으로 성공하고
     * 전체 목록이 돌아옵니다. 필터가 먹은 것과 화면상 구분이 되지 않아 응답으로 직접 검증합니다.
     * 판정은 refund_at 하나로 합니다(grade 는 환불돼도 바뀌지 않는 값입니다).
     */
    headCount() {
      return this.viewMode === "calendar" ? this.calCount : this.totalCount;
    },
    calMonthLabel() {
      return `${this.calYear}년 ${this.calMonth}월`;
    },
    calRange() {
      const mm = String(this.calMonth).padStart(2, "0");
      const last = new Date(this.calYear, this.calMonth, 0).getDate();
      return {
        start: `${this.calYear}-${mm}-01`,
        end: `${this.calYear}-${mm}-${String(last).padStart(2, "0")}`,
      };
    },
    calCells() {
      const y = this.calYear;
      const m = this.calMonth;
      const firstDow = new Date(y, m - 1, 1).getDay();
      const daysInMonth = new Date(y, m, 0).getDate();
      const daysInPrev = new Date(y, m - 1, 0).getDate();
      const now = new Date();
      const todayKey = [
        now.getFullYear(), String(now.getMonth() + 1).padStart(2, "0"), String(now.getDate()).padStart(2, "0"),
      ].join("-");
      const mm = String(m).padStart(2, "0");
      const cells = [];
      for (let i = firstDow - 1; i >= 0; i -= 1) {
        cells.push({ key: `p${i}`, d: daysInPrev - i, inMonth: false, isToday: false, day: null, date: null });
      }
      for (let d = 1; d <= daysInMonth; d += 1) {
        const date = `${y}-${mm}-${String(d).padStart(2, "0")}`;
        cells.push({ key: date, d, date, inMonth: true, isToday: date === todayKey, day: this.calDays[date] || null });
      }
      // 마지막 주를 7칸으로 채웁니다. 6주짜리 달도 있어 42칸으로 고정하지는 않습니다.
      let n = 1;
      while (cells.length % 7 !== 0) {
        cells.push({ key: `n${n}`, d: n, inMonth: false, isToday: false, day: null, date: null });
        n += 1;
      }
      return cells;
    },
    refundFilterIgnored() {
      const v = String(this.searchValues.refund_status || "").toUpperCase();
      if (v !== "NONE" && v !== "REFUNDED") return false;
      const want = v === "REFUNDED";
      return this.tableData.some((row) => pay.isRefunded(row) !== want);
    },
  },
  mounted() {
    this.getData();
    this.getStats();
  },
  methods: {
    refresh() {
      if (this.viewMode === "calendar") this.getCalendarData();
      else this.getData();
    },
    setViewMode(mode) {
      if (this.viewMode === mode) return;
      this.viewMode = mode;
      this.refresh();
    },
    shiftMonth(delta) {
      const d = new Date(this.calYear, this.calMonth - 1 + delta, 1);
      this.calYear = d.getFullYear();
      this.calMonth = d.getMonth() + 1;
      this.getCalendarData();
    },
    goThisMonth() {
      const now = new Date();
      this.calYear = now.getFullYear();
      this.calMonth = now.getMonth() + 1;
      this.getCalendarData();
    },
    // 달력에서 날짜를 누르면 그 하루로 기간 필터를 걸고 목록으로 넘어갑니다.
    // 뷰를 먼저 바꿔야 setFieldValue 가 일으키는 검색이 목록 쪽(getData)으로 갑니다.
    pickDay(date) {
      this.viewMode = "list";
      this.$refs.searchArea.setFieldValue("period", `${date} ~ ${date}`, { search: true });
    },
    cellTitle(cell) {
      const lines = [`${cell.date} · ${cell.day.count}건`];
      // 칸에는 순액만 보이므로 툴팁에서 결제·환불·순액을 모두 풀어 줍니다.
      cell.day.amounts.forEach((a) => {
        const net = this.formatCurrency(a.net, a.currency_code);
        if (a.refund > 0) {
          lines.push(`${a.currency_code} 순액 ${net} (결제 ${this.formatCurrency(a.amount, a.currency_code)} − 환불 ${this.formatCurrency(a.refund, a.currency_code)})`);
        } else {
          lines.push(`${a.currency_code} ${net}`);
        }
      });
      if (cell.day.refunds > 0) lines.push(`환불 ${cell.day.refunds}건`);
      // 툴팁 테마에 white-space 규칙이 없어 줄바꿈은 공백으로 뭉개집니다. 한 줄로 잇습니다.
      return lines.join(" · ");
    },
    // 검색 영역의 기간은 무시하고 보고 있는 달로 덮어씁니다. 그 외 조건(학습자·교재·결제수단·
    // 테스트·환불)과 탭은 목록과 똑같이 적용됩니다.
    calendarParams() {
      const params = { ...this.searchValues, start_date: this.calRange.start, end_date: this.calRange.end };
      if (this.activeTab !== "all") params.rental_status = this.activeTab;
      Object.keys(params).forEach((k) => {
        if (params[k] === "" || params[k] === null || params[k] === undefined) delete params[k];
      });
      return params;
    },
    async getCalendarData() {
      this.calLoading = true;
      try {
        const res = await this.$axios({
          method: "get", url: "/payment/admin/paymentCalendar", params: this.calendarParams(), loadingBar: true,
        });
        if (res.status === 200) this.applyCalendarResponse(res.data);
      } catch (error) {
        console.error("결제 달력 조회 실패:", error);
        this.$notifyVue({
          type: "danger", title: "결제 달력을 불러오는데 실패했습니다.",
          text: apiErrorMessage(error, "다시 시도해 주세요."),
        });
        this.calDays = {};
        this.calCount = 0;
        this.calTotals = [];
      } finally {
        this.calLoading = false;
      }
    },
    // 서버 집계 응답 → calDays. days[] 는 결제가 있는 날짜만 오고 각 행의 amounts 는 통화별입니다.
    applyCalendarResponse(data) {
      const days = {};
      (Array.isArray(data.days) ? data.days : []).forEach((d) => {
        const key = String(d?.date || "").slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return;
        days[key] = {
          count: Number(d.count) || 0,
          refunds: Number(d.refund_count) || 0,
          amounts: this.toCalendarAmounts(d.amounts),
        };
      });
      this.calDays = days;
      this.calCount = Number(data.total_count) || 0;
      this.calTotals = this.toCalendarAmounts(data.amount_totals);
    },
    // 통화별 행에 순액을 붙입니다. 순액은 netAmount 와 같은 규칙(서버 net_amount 우선, 없으면 뺄셈).
    // 정렬은 순액 내림차순 — 칸에 두 줄만 보일 때 큰 소득이 먼저 보이도록.
    toCalendarAmounts(raw) {
      return this.normalizeAmountTotals(raw)
        .map((a) => ({
          currency_code: a.currency_code.trim().toUpperCase(),
          amount: Number(a.amount) || 0,
          refund: Number(a.refund_amount) || 0,
          net: this.netAmount(a),
        }))
        .sort((a, b) => b.net - a.net);
    },
    async getData() {
      try {
        const params = { limit: this.itemsPerPage, page: this.currentPage, ...this.searchValues };
        if (this.activeTab !== "all") params.rental_status = this.activeTab;
        Object.keys(params).forEach((k) => {
          if (params[k] === "" || params[k] === null || params[k] === undefined) delete params[k];
        });
        const res = await this.$axios({ method: "get", url: "/payment/admin/paymentHistory", params, loadingBar: true });
        if (res.status === 200) {
          this.tableData = res.data.items || [];
          this.totalCount = res.data.total_count || 0;
          this.amountTotals = this.normalizeAmountTotals(res.data.amount_totals);
          this.selectedItems = [];
        }
      } catch (error) {
        console.error("결제 내역 조회 실패:", error);
        this.$notifyVue({
          type: "danger", title: "결제 내역을 불러오는데 실패했습니다.",
          // refund_status 는 허용값이 아니면 400 + detail 로 거부됩니다(조용히 무필터가 되지 않도록).
          // 공통 파서를 거쳐야 그 사유가 보입니다 — detail 은 message 키가 아닙니다.
          text: apiErrorMessage(error, "다시 시도해 주세요."),
        });
        this.tableData = [];
        this.totalCount = 0;
        this.amountTotals = [];
      }
    },
    // 서버는 count 내림차순 → 통화 코드순으로 정렬해 보내지만, 배열이 아니거나
    // 통화 코드가 빠진 행은 표기할 수 없으므로 여기서 걸러 둡니다.
    normalizeAmountTotals(raw) {
      if (!Array.isArray(raw)) return [];
      return raw.filter((t) => t && typeof t.currency_code === "string" && t.currency_code.trim());
    },
    // 순액. 서버가 net_amount 를 내려주면(docs/결제_영구소유_개편_관리자화면_가이드.md §3.3, 운영 미배포) 그 값을 쓰고 직접 빼지 않습니다 —
    // 순액 정의(수수료·쿠폰·타통화 환불 반영 등)가 바뀔 때 SQL 한 곳만 고치면 되도록 한 약속입니다.
    // 없으면(배포 전 서버) 같은 통화의 amount − refund_amount 로 되돌아갑니다.
    netAmount(t) {
      const server = Number(t?.net_amount);
      if (t?.net_amount !== null && t?.net_amount !== undefined && Number.isFinite(server)) return server;
      return (Number(t?.amount) || 0) - (Number(t?.refund_amount) || 0);
    },
    // KPI 카드용. 서버가 net_amount 를 아직 안 주거나 환불이 0이면 null → 병기 줄을 숨깁니다.
    revenueNet(kpi) {
      if (!kpi || kpi.net_amount === null || kpi.net_amount === undefined) return null;
      if (!(Number(kpi.refund_amount) > 0)) return null;
      return this.netAmount(kpi);
    },
    async getStats() {
      try {
        const res = await this.$axios({ method: "get", url: "/payment/paymentStats" });
        if (res.status === 200) {
          this.stats = res.data;
          // statusCounts 는 paymentHistory 의 total_count 와 동일 기준(테스트 결제 포함)으로 집계됩니다.
          // 매출 KPI(totalRevenue·monthRevenue)만 테스트 결제를 제외하므로 건수와 금액의 모수가 다릅니다 — 버그가 아닙니다.
          const counts = res.data.statusCounts || {};
          this.tabs[0].count = counts.total || 0;
          this.tabs[1].count = counts.active || 0;
          this.tabs[2].count = counts.expired || 0;
          this.tabs[3].count = counts.scheduled || 0;
        }
      } catch (error) {
        console.error("통계 조회 실패:", error);
      }
    },
    async getInvoiceDetails(payment_idx, user_idx) {
      try {
        const res = await this.$axios({
          method: "get", url: "/payment/paymentDetail",
          params: { payment_idx, user_idx }, loadingBar: true,
        });
        if (res.status === 200) {
          this.invoiceDetails = res.data;
          return res.data;
        }
      } catch (error) {
        console.error("인보이스 상세 조회 실패:", error);
        this.$notifyVue({ type: "danger", title: "인보이스 정보를 불러오는데 실패했습니다." });
        return null;
      }
    },
    handleSearch(values) {
      this.searchValues = values;
      this.currentPage = 1;
      // 결제수단 선택이 '무료/사은품 제외'와 정확히 일치하면 토글도 켜진 상태로 동기화
      const selected = (values.payment_method || "").split(",").filter(Boolean).sort().join(",");
      this.excludeFree = selected === [...this.nonFreeMethods].sort().join(",");
      this.refresh();
    },
    handleTabChange(tab) {
      this.activeTab = tab;
      this.currentPage = 1;
      this.refresh();
    },
    handleExcludeFreeChange() {
      // 토글 ON → 무료/사은품 외 전체 선택, OFF → 선택 해제(전체)
      const value = this.excludeFree ? [...this.nonFreeMethods] : [];
      this.$refs.searchArea.setFieldValue("payment_method", value, { search: true });
    },
    async openInvoiceModal(item) {
      this.selectedPayment = { ...item };
      const details = await this.getInvoiceDetails(item.payment_idx, item.learner_idx);
      if (details) {
        this.invoiceDetails = details;
        this.showInvoiceModal = true;
      }
    },
    closeInvoiceModal() {
      this.showInvoiceModal = false;
      this.selectedPayment = null;
      this.invoiceDetails = null;
    },
    async openPgFeeModal() {
      this.showPgFeeModal = true;
      this.pgFeeLoading = true;
      this.pgFeeInfo = null;
      try {
        const res = await this.$axios({ method: "get", url: "/payment/pgFeeInfo", loadingBar: false });
        if (res.status === 200) this.pgFeeInfo = res.data;
      } catch (error) {
        console.error("PG 수수료 정보 조회 실패:", error);
        this.$notifyVue({ type: "danger", title: "PG 수수료 정보를 불러오는데 실패했습니다." });
      } finally {
        this.pgFeeLoading = false;
      }
    },
    closePgFeeModal() {
      this.showPgFeeModal = false;
      this.pgFeeInfo = null;
    },
    getPgDisplayName(pg) {
      return { toss: "Toss Payments", lemonsqueezy: "Lemonsqueezy", apple: "App Store", google: "Google Play" }[pg]
        || pg.charAt(0).toUpperCase() + pg.slice(1);
    },
    getPgIcon(pg) {
      return { toss: "account_balance", lemonsqueezy: "payment", apple: "apple", google: "android" }[pg] || "payment";
    },
    // 소수 배율(0.025)·퍼센트(2.5) 어느 쪽으로 내려와도 같은 값을 그립니다 — pay.feeRatePercent 참고.
    formatFeePercent(fee) { return pay.feeRatePercent(fee).toFixed(1) + "%"; },
    async downloadInvoicePDF(invoiceIdx) {
      try {
        await this.$axios_download(`/sale-invoice/download/${invoiceIdx}`, {
          method: "get",
          fileName: `receipt_${invoiceIdx}.pdf`,
        });
        this.$notifyVue({ type: "success", title: "다운로드 완료", text: "인보이스 PDF를 다운로드했습니다." });
      } catch (error) {
        console.error("인보이스 PDF 다운로드 실패:", error);
        this.$notifyVue({ type: "danger", title: "다운로드 실패", text: "인보이스 PDF 파일 다운로드에 실패했습니다." });
      }
    },
    async downloadExcel() {
      try {
        const params = { limit: 9999, page: 1, ...this.searchValues };
        if (this.activeTab !== "all") params.rental_status = this.activeTab;
        Object.keys(params).forEach((k) => {
          if (params[k] === "" || params[k] === null || params[k] === undefined) delete params[k];
        });
        const res = await this.$axios({
          method: "get", url: "/payment/admin/paymentHistory", params, loadingBar: true,
        });
        if (res.status === 200) {
          const all = res.data.items || [];
          const excelData = all.map((item, index) => ({
            번호: all.length - index,
            학습자: item.learner_name,
            이메일: item.learner_email || "-",
            교재: item.textbook_title,
            바코드: item.barcode,
            구분: item.purchase_type ? this.purchaseTypeLabel(item.purchase_type) : "-",
            등급: this.gradeLabel(item.grade) || "-",
            결제금액: this.formatCurrency(item.amount, item.currency_code),
            쿠폰할인: this.visibleCouponAmount(item) > 0
              ? this.formatCurrency(this.visibleCouponAmount(item), item.currency_code)
              : "-",
            // 차감액이 없으면 엑셀에서 결제금액이 정가와 안 맞는 이유를 알 수 없습니다.
            // 화면과 달리 툴팁이 없어 여기서는 열로만 설명됩니다.
            업그레이드차감: this.visibleCreditedAmount(item) > 0
              ? this.formatCurrency(this.visibleCreditedAmount(item), item.currency_code)
              : "-",
            결제수단: this.getPaymentMethodLabel(item.payment_method),
            // 엑셀에는 뱃지가 없어 그냥 두면 테스트 결제가 실적에 합산됩니다.
            // 미배포 응답(undefined)을 "실결제"로 단정하지 않으려고 아닌 쪽은 '-' 로 둡니다.
            결제구분: this.isTestRow(item) ? "테스트" : "-",
            결제일: this.formatDateTime(item.payment_date),
            시작일: this.formatDate(item.start_date),
            // 9999-12-31 센티널이 엑셀에 그대로 찍히지 않도록 화면과 같은 규칙을 씁니다.
            종료일: this.endDateText(item),
            // 묶음 구매의 기능만 끝난 행은 날짜가 null 이라 그냥 두면 '-' 로 빠집니다(2차답변 §4-1).
            기능만료일: this.featureEndText(item) || (this.isFeatureEndedRow(item) ? "종료" : "-"),
            // 환불 건은 rental_status 가 EXPIRED 로만 찍혀 만료와 구분되지 않습니다.
            // 화면과 같은 규칙(refund_at)으로 따로 적습니다.
            // 전환 종료도 같은 이유로 풀어 적습니다 — 엑셀에는 뱃지도 툴팁도 없어서,
            // 그냥 두면 "만료인데 종료일은 미래"인 행이 설명 없이 남습니다.
            상태: this.isRefunded(item)
              ? "환불"
              : this.isSupersededRow(item)
                ? `${this.getRentalStatusLabel(item.rental_status)}(${this.SUPERSEDED_TEXT})`
                : this.getRentalStatusLabel(item.rental_status),
            환불일: this.isRefunded(item) ? this.formatDateTime(item.refund_at) : "-",
            환불금액: item.refund_amount != null
              ? this.formatCurrency(item.refund_amount, item.refund_currency_code || item.currency_code)
              : "-",
          }));
          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(excelData);
          // excelData 의 키 순서와 1:1 로 맞춰야 합니다(현재 19열).
          ws["!cols"] = [
            // 번호      학습자      이메일      교재        바코드      구분
            { wch: 8 }, { wch: 12 }, { wch: 24 }, { wch: 20 }, { wch: 15 }, { wch: 12 },
            // 등급      결제금액    쿠폰할인    업그레이드차감  결제수단    결제구분
            { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 10 },
            // 결제일    시작일      종료일      기능만료일  상태        환불일      환불금액
            { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 18 }, { wch: 12 },
          ];
          // 하단 합계 행. 화면과 같은 amount_totals 를 쓰므로 통화별로 한 줄씩이며
          // 환율 환산은 하지 않습니다(docs/결제_영구소유_개편_관리자화면_가이드.md §3.2). 열 위치는 위 !cols 순서와 같습니다:
          // 0 번호 · 1 학습자 · 7 결제금액 · 18 환불금액. 미배포 서버는 배열이 비어 행이 붙지 않습니다.
          // ⚠ 9,999건을 넘는 조회는 위 목록이 잘리지만 합계는 전체 기준입니다.
          const totals = this.normalizeAmountTotals(res.data.amount_totals);
          if (totals.length > 0) {
            const rows = totals.map((t) => {
              const row = new Array(19).fill("");
              row[0] = "합계";
              row[1] = `${t.currency_code} ${t.count}건`;
              row[7] = this.formatCurrency(t.amount, t.currency_code);
              row[18] = Number(t.refund_amount) > 0 ? this.formatCurrency(t.refund_amount, t.currency_code) : "-";
              return row;
            });
            XLSX.utils.sheet_add_aoa(ws, [new Array(19).fill(""), ...rows], { origin: -1 });
          }
          XLSX.utils.book_append_sheet(wb, ws, "결제 내역");
          XLSX.writeFile(wb, `결제_내역_${new Date().toISOString().split("T")[0]}.xlsx`);
          this.$notifyVue({
            type: "success", title: "다운로드 완료",
            text: `${all.length}건의 결제 내역을 다운로드했습니다.`,
          });
        }
      } catch (error) {
        console.error("엑셀 다운로드 실패:", error);
        this.$notifyVue({ type: "danger", title: "다운로드 실패", text: "엑셀 파일 생성에 실패했습니다." });
      }
    },
    // 통화 기호·자릿수는 통화 코드에서 끌어옵니다(src/utils/currency.js).
    formatCurrency(amount, currencyCode) { return formatMoney(amount, currencyCode); },
    formatDate(s) {
      if (!s) return "-";
      return new Date(s).toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" });
    },
    formatDateTime(s) {
      if (!s) return "-";
      return new Date(s).toLocaleString("ko-KR", {
        year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
      });
    },
    getPaymentMethodLabel(c) {
      // Stripe(S) 는 도입 검토만 하고 결제가 한 건도 이뤄진 적이 없어 라벨도 두지 않습니다.
      // 혹시 값이 오면 폴백으로 코드가 그대로 보이므로 "없는 결제수단"을 화면이 지어내지는 않습니다.
      return { A: "Apple Pay", G: "Google Pay", T: "Toss", F: "무료/사은품", L: "Lemonsqueezy", V: "기관 바우처" }[c] || c;
    },
    getPaymentMethodIcon(c) {
      return { A: "apple", G: "android", T: "account_balance", F: "card_giftcard", L: "payment", V: "confirmation_number" }[c] || "payment";
    },
    // ── 영구소유 개편 표시 규칙 (src/utils/paymentDisplay.js) ──
    // 목록과 인보이스 모달이 같은 행을 그리므로 판정은 공용 유틸 한 곳에만 둡니다.
    isPermanentRow(row) { return pay.isPermanentRow(row); },
    hasNoPlan(row) { return pay.hasNoPlan(row); },
    hasPurchaseType(row) { return pay.hasPurchaseType(row); },
    isUpgradeRow(row) { return pay.isUpgradeRow(row); },
    purchaseTypeLabel(t) { return pay.purchaseTypeLabel(t); },
    purchaseTypePillKind(t) { return pay.purchaseTypePillKind(t); },
    purchaseTypeIcon(t) { return pay.purchaseTypeIcon(t); },
    gradeLabel(g) { return pay.gradeLabel(g); },
    gradePillKind(g) { return pay.gradePillKind(g); },
    visibleCouponAmount(row) { return pay.visibleCouponAmount(row); },
    isTestRow(row) { return pay.isTestRow(row); },
    // 환불 판정·버튼 노출 조건도 같은 곳(paymentDisplay)에 둡니다 — grade 로 판정하지 않기 위함입니다.
    isRefunded(row) { return pay.isRefunded(row); },
    canRefundSync(row) { return pay.canRefundSync(row); },
    canRefundExec(row) { return pay.canRefundExec(row); },
    // 환불 기한 14일(docs/환불_즉시반영_관리자화면_가이드.md §3.3). 잠금 여부와 툴팁 문구를 같은 곳에서 가져와 목록·모달이 어긋나지 않게 합니다.
    isRefundWindowClosed(row) { return pay.isRefundWindowClosed(row); },
    refundWindowClosedText(row) { return pay.refundWindowClosedText(row); },
    lookupSourceLabel(s) { return pay.lookupSourceLabel(s); },
    // 상위 등급 전환으로 끝난 이용권(정책 §5). 목록·인보이스 모달이 같은 판정을 써야 합니다.
    isSupersededRow(row) { return pay.isSupersededRow(row); },
    visibleCreditedAmount(row) { return pay.visibleCreditedAmount(row); },
    isFeatureEndedRow(row) { return pay.isFeatureEndedRow(row); },
    /**
     * 전환 종료 뱃지의 툴팁. 원인이 된 상위 결제 코드가 있으면 함께 붙입니다(2차답변 §4).
     * CS 응대에서 다음 질문이 늘 "그럼 어느 결제 때문인가"라, 그 답을 뱃지에 실어 둡니다.
     */
    supersededTitle(row) {
      const code = pay.supersededByPayCode(row);
      return code ? `${pay.SUPERSEDED_HINT} 전환한 상위 결제: ${code}` : pay.SUPERSEDED_HINT;
    },
    // 이 화면의 formatDate(ko-KR 2-digit)를 묶어 씁니다.
    endDateText(row) { return pay.endDateText(row, this.formatDate); },
    featureEndText(row) { return pay.featureEndText(row, this.formatDate); },
    // 업그레이드 결제는 start~end 자체가 기능 이용 기간이라 부기하면 같은 값을 두 번 찍습니다.
    featurePeriodNote(row) {
      if (pay.isUpgradeRow(row)) return "";
      return pay.featurePeriodText(row, this.formatDate);
    },
    getRentalStatusPillKind(s) {
      return { ACTIVE: "ok", EXPIRED: "default", SCHEDULED: "info" }[s] || "default";
    },
    getRentalStatusLabel(s) {
      return { ACTIVE: "활성", EXPIRED: "만료", SCHEDULED: "예정" }[s] || s;
    },
    openRefundExecModal(item) {
      this.refundExecItem = { ...item };
      this.refundExecForm = {
        cancel_reason: "", isVirtualAccount: false,
        refund_receive_account: { bank: "", accountNumber: "", holderName: "" },
      };
      this.relatedPayments = [];
      // 다른 결제로 넘어왔을 때 앞 결제의 가드 안내가 남아 있으면 엉뚱한 결제를 가리킵니다.
      this.refundOrderGuard = null;
      this.showRefundExecModal = true;
      // 부가 안내용이라 await 하지 않습니다. 모달은 즉시 뜨고 목록은 나중에 채워집니다.
      this.loadRelatedPayments(item);
    },
    closeRefundExecModal() {
      this.showRefundExecModal = false;
      this.refundExecItem = null;
      this.refundExecLoading = false;
      this.relatedPayments = [];
      this.refundOrderGuard = null;
    },
    // 환불 단위는 결제 건입니다. 도서 결제와 업그레이드 결제는 별개라 각각 환불해야 하는데,
    // payment 단위 연관 조회 API 가 없어 이름·교재명으로 좁혀 받은 뒤
    // learner_idx·textbook_idx 로 정확히 일치하는 행만 남깁니다(동명이인·동명교재 방어).
    async loadRelatedPayments(row) {
      if (!row?.learner_idx || !row?.textbook_idx) return;
      try {
        const res = await this.$axios({
          method: "get",
          url: "/payment/admin/paymentHistory",
          params: {
            // 이 파라미터는 이제 이름 OR 이메일로 찾습니다.
            // 이메일이 있으면 동명이인 계정이 섞이지 않아 받아오는 행이 줄어듭니다.
            learner_name: row.learner_email || row.learner_name,
            textbook_title: row.textbook_title,
            limit: 50,
            page: 1,
          },
          loadingBar: false,
        });
        this.relatedPayments = (res?.data?.items || []).filter(
          (it) =>
            it.payment_idx !== row.payment_idx &&
            it.learner_idx === row.learner_idx &&
            it.textbook_idx === row.textbook_idx &&
            !it.refund_at
        );
      } catch (error) {
        // 안내용 부가 정보입니다. 조회가 실패해도 환불 자체는 진행할 수 있어야 합니다.
        console.warn("동반 환불 대상 조회 실패:", error);
      }
    },
    /**
     * 환불 실행. 결제사에 취소를 걸어 실제로 돈을 돌려줍니다 — 되돌릴 수 없습니다.
     *
     * 두 결제사는 보내는 식별자만 다릅니다.
     *   T  payment_key(pay_code) — 토스가 결제를 식별하는 키
     *   L  payment_idx           — 결제 이력의 row key. 서버가 LS 주문을 찾아 취소합니다.
     * 필드는 둘 다 snake_case 이고, cancel_amount 는 보내지 않습니다(생략 = 전액 취소).
     * 부분 금액 입력을 두지 않는 이유는 모달 주석에 적어 두었습니다(운영 원칙이 전액 환불).
     *
     * cancel_amount 를 보내지 않으므로 docs/환불_즉시반영_관리자화면_가이드.md §3.3 의 LS 새 400(결제 금액 초과 · 최소 0.01
     * 미만)은 이 화면에서 날 수 없습니다. 그래서 전용 분기를 두지 않고, 혹시 오면 아래 기본
     * 분기가 서버 detail 원문을 그대로 띄웁니다.
     */
    async submitRefundExec() {
      if (!this.refundExecForm.cancel_reason.trim()) {
        this.$notifyVue({ type: "warning", title: "취소 사유를 입력해주세요." });
        return;
      }
      const method = this.refundExecMethod;
      const confirmed = window.confirm(
        `${this.refundExecMethodLabel} 결제를 전액(${this.formatCurrency(this.refundExecItem?.amount, this.refundExecItem?.currency_code)}) 환불합니다.\n` +
          "결제사에 취소를 요청하며 되돌릴 수 없습니다.\n" +
          "이 결제로 지급된 도서 열람권(영구 포함)과 기능 이용권이 모두 즉시 정지됩니다."
      );
      if (!confirmed) return;

      // 앞선 시도의 가드 안내는 이번 결과로 덮어씁니다. 남겨 두면 성공했는데도 "환불할 수 없다"가 붙어 있습니다.
      this.refundOrderGuard = null;
      this.refundExecLoading = true;
      try {
        const cancel_reason = this.refundExecForm.cancel_reason.trim();
        let url = "/lemonsqueezy/cancel";
        let payload = { payment_idx: this.refundExecItem.payment_idx, cancel_reason };

        if (method === "T") {
          url = "/toss/cancel";
          payload = { payment_key: this.refundExecItem.pay_code, cancel_reason };
          if (this.refundExecForm.isVirtualAccount) {
            const { bank, accountNumber, holderName } = this.refundExecForm.refund_receive_account;
            if (!bank || !accountNumber || !holderName) {
              this.$notifyVue({ type: "warning", title: "가상계좌 환불 계좌 정보를 모두 입력해주세요." });
              this.refundExecLoading = false;
              return;
            }
            payload.refund_receive_account = { bank, accountNumber, holderName };
          }
        }

        const res = await this.$axios({ method: "post", url, data: payload, loadingBar: true });
        if (res.data?.success) {
          // LS 응답의 applied 는 "반영이 남았는가"가 아닙니다(백엔드 회신 2026-08-31).
          // 200 경로에서 applied=false 가 나오는 경우는 reason='already_applied' 하나뿐이고,
          // 이는 웹훅이 먼저 반영한 경쟁 상황이라 환불도 서버 반영도 끝난 완료 상태입니다.
          // 반영이 남은 상태는 애초에 200 으로 오지 않습니다(4xx). applied=false 를 경고로 띄우면
          // 정상 완료된 건을 문제처럼 보이게 하므로 두 reason 모두 완료로 봅니다.
          //
          // 다만 그 두 값이 아닌 reason 은 우리가 모르는 상태라 완료로 단정하지 않습니다 —
          // 서버가 새 분기를 추가했을 때 화면이 조용히 오안내하지 않도록 남겨 둡니다.
          const unknownReason = method === "L" && !LS_CANCEL_REASONS.includes(res.data.reason);
          this.$notifyVue({
            type: unknownReason ? "warning" : "success",
            title: unknownReason ? "환불 결과를 확인해 주세요" : "환불 완료",
            text: unknownReason
              ? `서버가 처음 보는 결과를 보냈습니다(reason: ${res.data.reason ?? "없음"}) — 목록에서 환불 상태를 확인해 주세요.`
              : res.data.message || "결제가 성공적으로 취소되었습니다.",
          });
          this.closeRefundExecModal();
          this.getData();
          this.getStats();
        }
      } catch (error) {
        const status = apiErrorStatus(error);
        const detail = error.response?.data?.detail;
        // POST /lemonsqueezy/cancel 은 2026-08-31 신설이라 운영에는 아직 없을 수 있습니다.
        // 라우트가 없으면 FastAPI 가 detail: "Not Found" 로 답하고, 구현된 404 는 한글 문구가 옵니다.
        if (method === "L" && (status === 405 || status === 501 || (status === 404 && (!detail || detail === "Not Found")))) {
          console.info("LemonSqueezy 환불 API 미배포: POST /lemonsqueezy/cancel");
          this.$notifyVue({
            type: "warning",
            title: "LemonSqueezy 환불 API가 아직 준비되지 않았습니다",
            text: "배포 전까지는 LemonSqueezy 대시보드에서 직접 환불해 주세요.",
          });
          return;
        }
        // 409 는 재시도해도 결과가 같습니다(이미 환불됨 / LS 결제가 아님 / 주문 식별자 없음 /
        // 환불 기한 14일 경과 — docs/환불_즉시반영_관리자화면_가이드.md §3.3). detail 이 각 상황의 정식 경로를 안내하는 문장이라
        // 그대로 노출합니다. 기한 경과는 화면이 미리 버튼을 잠그지만 자정 경계 등으로 여기까지 올 수
        // 있고, 그때 정본은 서버라 detail(결제일·기한 포함)을 그대로 띄웁니다. 별도 분기를 두지 않는
        // 이유: 다음에 할 일이 없는 "닫고 끝" 409 라 순서 가드처럼 모달을 붙들 필요가 없습니다.
        if (status === 409) {
          //
          // 다만 등급 전환 환불 순서 가드(정책 §6-3)만은 "닫고 끝"이 아닙니다.
          // 안내 문장 안에 **먼저 환불할 상위 결제의 pay_code** 가 들어 있어서, 토스트로 흘려보내면
          // 몇 초 뒤 사라진 그 코드를 운영자가 다시 볼 방법이 없습니다. 그러면 사유를 모른 채
          // 재시도만 반복하다 결국 결제사 대시보드에서 직접 취소하게 되고, 그건 가드를 우회한 것이라
          // 차감분 회수가 사후 처리로 밀립니다. 모달을 닫지 않고 안내를 붙여 둡니다.
          //
          // 목록을 새로고침하지 않는 이유: 이 가드는 PG 취소 API 를 부르기 **전**에 걸려서
          // 서버에서 바뀐 것이 아무것도 없습니다. 새로고침하면 뭔가 처리된 것처럼 읽힙니다.
          if (pay.isRefundOrderGuard(detail)) {
            this.refundOrderGuard = {
              detail: apiErrorMessage(error, "상위 등급 전환 결제를 먼저 환불해 주세요."),
              payCode: pay.refundOrderGuardPayCode(detail),
            };
            return;
          }
          this.$notifyVue({
            type: "warning", title: "환불할 수 없습니다",
            text: apiErrorMessage(error, "결제 상태를 확인해 주세요."),
          });
          this.closeRefundExecModal();
          this.getData();
          this.getStats();
          return;
        }
        console.error("환불 실행 실패:", error);
        this.$notifyVue({
          type: "danger", title: "환불 처리 실패",
          text: apiErrorMessage(error, "다시 시도해 주세요."),
        });
      } finally {
        this.refundExecLoading = false;
      }
    },
    /**
     * 순서 가드가 알려준 상위 결제 코드를 클립보드로. 상위 결제가 이 화면 목록에 없으면
     * (다른 교재로 잘못 매칭됐거나 50건 밖) 운영자가 이 코드로 찾아야 해서 손으로 옮겨 적게 두지 않습니다.
     * navigator.clipboard 는 보안 컨텍스트에서만 동작하므로 execCommand 폴백을 둡니다(CouponManager 와 동일).
     */
    async copyPayCode(code) {
      try {
        await navigator.clipboard.writeText(code);
      } catch (error) {
        const ta = document.createElement("textarea");
        ta.value = code; document.body.appendChild(ta);
        ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      this.$notifyVue({ type: "success", title: "결제 코드가 복사되었습니다." });
    },
    // 테스트 결제는 프로덕션 스토어에 없어 Apple 은 404, Google 은 "목록에 없음"으로 답합니다.
    // 화면에는 "환불 아님"처럼 보이지만 실제로는 판정 불가라 누르기 전에 알려 줍니다.
    refundSyncTooltip(row) {
      const base = "환불 즉시 반영 — 스토어에 환불 여부를 물어 서버에 기록합니다";
      if (!pay.isTestRow(row)) return base;
      return `${base}. ⚠ 테스트(샌드박스) 결제는 프로덕션 스토어에 없어 결과가 "환불 아님"이 아니라 판정 불가입니다`;
    },
    /**
     * 환불 즉시 반영. 스토어에 환불 여부를 묻고, 환불됐는데 서버에 반영되지 않았으면 그 자리에서 회수합니다.
     *
     * 조회만 하는 안전한 멱등 API 라 확인 대화상자를 두지 않습니다. 같은 건을 여러 번 눌러도
     * 이미 반영된 건은 200 + already_refunded 로 돌아올 뿐입니다(에러가 아닙니다).
     *
     * days 는 보내지 않습니다 — 기본 30일이 최대값이고, 좁히면 그 창 밖의 환불이 "판정 불가"로 나옵니다.
     *
     * @param {object} row 결제 이력의 한 행
     * @param {boolean} force 이미 반영된 건의 강제 재반영(§3.2). 기본 버튼에서는 절대 true 로 오지 않습니다.
     */
    async runRefundSync(row, force = false) {
      if (!row?.payment_idx) return;
      this.refundSyncBusyIdx = row.payment_idx;
      this.refundSyncLoading = true;
      try {
        const res = await this.$axios({
          method: "post",
          url: `/payment/admin/refundSync/${row.payment_idx}`,
          params: force ? { force: true } : {},
          loadingBar: true,
        });
        this.refundSyncItem = { ...row };
        this.refundSyncResult = res.data || null;
        this.showRefundSyncModal = true;

        // 이번 호출로 반영됐다면 목록의 상태·환불일이 이미 낡았습니다.
        if (res.data?.auto_processed) {
          this.getData();
          this.getStats();
        }
      } catch (error) {
        const status = apiErrorStatus(error);
        const detail = error.response?.data?.detail;
        // POST /payment/admin/refundSync 는 2026-08-28 신설이라 운영에는 아직 없을 수 있습니다.
        // 구현된 404 는 "해당 결제 내역을 찾을 수 없습니다." 라 FastAPI 기본 문구와 구분됩니다.
        if (status === 405 || status === 501 || (status === 404 && (!detail || detail === "Not Found"))) {
          console.info("환불 즉시 반영 API 미배포: POST /payment/admin/refundSync");
          this.$notifyVue({
            type: "warning",
            title: "환불 즉시 반영 API가 아직 준비되지 않았습니다",
            text: "배포 전까지는 폴백 배치(10분 주기)가 반영할 때까지 기다려야 합니다.",
          });
          return;
        }
        // 409 는 결제수단이 대상이 아니라는 뜻이고, detail 이 각 수단의 정식 경로를 안내합니다.
        // 재시도해도 같은 결과라 빨간 에러가 아니라 안내로 띄우고 문구를 그대로 보여줍니다.
        if (status === 409) {
          this.$notifyVue({
            type: "warning", title: "이 결제는 즉시 반영 대상이 아닙니다",
            text: apiErrorMessage(error, "결제수단을 확인해 주세요."),
          });
          return;
        }
        if (status === 404) {
          this.$notifyVue({
            type: "warning", title: "이미 삭제된 결제입니다.",
            text: apiErrorMessage(error, "목록을 새로 고쳤습니다."),
          });
          this.getData();
          return;
        }
        console.error("환불 즉시 반영 실패:", error);
        this.$notifyVue({
          type: "danger", title: "환불 즉시 반영 실패",
          text: apiErrorMessage(error, "다시 시도해 주세요."),
        });
      } finally {
        this.refundSyncBusyIdx = null;
        this.refundSyncLoading = false;
      }
    },
    // 강제 재반영은 학습자에게 환불 푸시를 다시 보냅니다. 기본 버튼과 분리해 확인을 한 번 더 받습니다.
    async runRefundSyncForce() {
      const row = this.refundSyncItem;
      if (!row) return;
      const confirmed = window.confirm(
        "이미 환불로 기록된 건을 강제로 다시 반영합니다.\n" +
          "학습자에게 환불 푸시가 다시 발송됩니다.\n" +
          "이용권이 회수되지 않은 경우에만 사용해 주세요."
      );
      if (!confirmed) return;
      await this.runRefundSync(row, true);
    },
    closeRefundSyncModal() {
      this.showRefundSyncModal = false;
      this.refundSyncItem = null;
      this.refundSyncResult = null;
    },
    openFreeItemModal() {
      this.freeItemForm = {
        isbns: [""], emails: [""], plan_months: 6,
        grade: "basic", pay_code: "AUTO_GENERATED",
        amount: 0, coupon_amount: 0, payment_method: "F",
      };
      this.freeItemResults = [];
      this.showFreeItemModal = true;
      this.getPricingOptions();
    },
    // 가격표는 인증이 필요 없고 잘 바뀌지 않아 세션 중 한 번만 받습니다.
    // 실패해도 모달은 쓸 수 있어야 하므로(고정값 폴백) 오류를 띄우지 않습니다.
    async getPricingOptions() {
      if (this.pricingOptions.length || this.pricingLoading) return;
      this.pricingLoading = true;
      try {
        const res = await this.$axios({ method: "get", url: "/textbook/productPricing", loadingBar: false });
        const rows = Array.isArray(res?.data) ? res.data : [];
        // addon 행은 도서 없이 기능만 주는 상품이라 무료 지급에 쓰면 안 됩니다.
        // product_kind 가 없는 개편 전 응답은 전 행을 total 로 봅니다.
        this.pricingOptions = rows.filter(
          (r) => r?.product_kind !== "addon" && typeof r?.product_pricing_idx === "number"
        );
      } catch (error) {
        console.warn("상품 가격표 조회 실패 — 고정값으로 전송합니다:", error);
      } finally {
        this.pricingLoading = false;
      }
    },
    closeFreeItemModal() {
      this.showFreeItemModal = false;
      this.freeItemLoading = false;
      this.freeItemResults = [];
    },
    // 입력은 남기고 결과만 지웁니다. 스킵된 건의 이메일만 고쳐 다시 보내는 흐름을 위한 것입니다.
    resetFreeItemResults() {
      this.freeItemResults = [];
    },
    // 분류의 1차 기준은 payment_id 입니다(생성됐으면 non-null).
    // 정규식은 "대상 없음(입력 오류)"만 빨강으로 빼는 색상 용도입니다 —
    // 서버 문구가 바뀌어도 warn 으로 떨어질 뿐이라 화면이 깨지지 않습니다.
    freeResultKind(r) {
      if (r?.payment_id) return "ok";
      if (/찾을 수 없습니다/.test(r?.message || "")) return "err";
      return "warn";
    },
    freeResultIcon(r) {
      return { ok: "check_circle", warn: "info", err: "error_outline" }[this.freeResultKind(r)];
    },
    addIsbnRow() { this.freeItemForm.isbns.push(""); },
    removeIsbnRow(i) {
      if (this.freeItemForm.isbns.length > 1) this.freeItemForm.isbns.splice(i, 1);
    },
    addEmailRow() { this.freeItemForm.emails.push(""); },
    removeEmailRow(i) {
      if (this.freeItemForm.emails.length > 1) this.freeItemForm.emails.splice(i, 1);
    },
    async submitFreeItem() {
      const isbns = this.freeItemForm.isbns.map((v) => v.trim()).filter(Boolean);
      const emails = this.freeItemForm.emails.map((v) => v.trim()).filter(Boolean);
      if (isbns.length === 0) { this.$notifyVue({ type: "warning", title: "ISBN을 1개 이상 입력해주세요." }); return; }
      if (emails.length === 0) { this.$notifyVue({ type: "warning", title: "사용자 이메일을 1개 이상 입력해주세요." }); return; }
      if (!this.freeItemForm.plan_months || this.freeItemForm.plan_months < 1) {
        this.$notifyVue({ type: "warning", title: "기능 이용 기간(개월)을 1 이상으로 입력해주세요." });
        return;
      }
      const confirmed = window.confirm(`ISBN ${isbns.length}건 × 이메일 ${emails.length}건에 대해 무료 등록을 진행하시겠습니까?`);
      if (!confirmed) return;

      this.freeItemLoading = true;
      try {
        const payload = {
          isbns, emails,
          product_pricing_idx: this.resolvedPricingIdx,
          pay_code: this.freeItemForm.pay_code,
          amount: this.freeItemForm.amount,
          coupon_amount: this.freeItemForm.coupon_amount,
          currency_code: this.resolvedCurrencyCode,
          payment_method: this.freeItemForm.payment_method,
          plan_months: this.freeItemForm.plan_months,
        };
        const res = await this.$axios({ method: "post", url: "/payment/admin/insertFreePayment", data: payload, loadingBar: true });
        if (res.status === 200) {
          // HTTP 200 이어도 행마다 결과가 다릅니다(이미 보유해서 skip / 바코드·학습자 없음).
          // 응답을 버리면 전 행이 skip 돼도 "등록 완료" 로만 보입니다.
          const rows = Array.isArray(res.data?.data) ? res.data.data : [];
          this.freeItemResults = rows;

          if (rows.length === 0) {
            // data[] 를 주지 않는 구버전 응답 — 종전 동작을 유지합니다.
            this.$notifyVue({
              type: "success", title: "등록 완료",
              text: res.data?.message || "무료/사은품 결제가 등록되었습니다.",
            });
            this.closeFreeItemModal();
          } else {
            const sum = this.freeResultSummary;
            const skipped = sum.warn + sum.err;
            this.$notifyVue({
              type: sum.ok > 0 ? (skipped > 0 ? "warning" : "success") : "warning",
              title: sum.ok > 0 ? "등록 완료" : "등록된 건이 없습니다",
              text: `성공 ${sum.ok} · 건너뜀 ${sum.warn} · 실패 ${sum.err}`,
            });
            // 스킵·실패가 있으면 사유를 볼 수 있게 모달을 열어 둡니다.
            if (skipped === 0) this.closeFreeItemModal();
          }
          this.getData();
          this.getStats();
        }
      } catch (error) {
        console.error("무료/사은품 등록 실패:", error);
        this.$notifyVue({
          type: "danger", title: "등록 실패",
          // 이 라우트의 400 은 detail 이 객체({ message })라 공통 파서를 거쳐야 사유가 보입니다.
          text: apiErrorMessage(error, "다시 시도해 주세요."),
        });
      } finally {
        this.freeItemLoading = false;
      }
    },
    openFreeDeleteModal(item) {
      this.freeDeleteItem = { ...item };
      this.freeDeleteLoading = false;
      this.showFreeDeleteModal = true;
    },
    closeFreeDeleteModal() {
      this.showFreeDeleteModal = false;
      this.freeDeleteItem = null;
      this.freeDeleteLoading = false;
    },
    async submitFreeDelete() {
      if (!this.freeDeleteItem) return;
      // URL 자체가 무료 전용이지만, 화면 상태가 꼬여 유료 건이 들어오는 사고를 한 번 더 막습니다.
      if (this.freeDeleteItem.payment_method !== "F") {
        this.$notifyVue({ type: "warning", title: "무료/사은품 결제만 삭제할 수 있습니다." });
        return;
      }

      this.freeDeleteLoading = true;
      try {
        const res = await this.$axios({
          method: "delete",
          url: `/payment/admin/freePayment/${this.freeDeleteItem.payment_idx}`,
          loadingBar: true,
        });
        // standard/premium 지급 건은 이용권이 book + feature 2행이라 plan_idxs 가 2개일 수 있습니다.
        const planIdxs = res?.data?.deleted?.plan_idxs || [];
        const planNote = planIdxs.length > 1 ? ` (이용권 ${planIdxs.length}건 · 도서 + 기능)` : "";
        this.$notifyVue({
          type: "success", title: "삭제 완료",
          text: `${this.freeDeleteItem.learner_name} · ${this.freeDeleteItem.textbook_title} 무료/사은품 기록을 삭제했습니다.${planNote}`,
        });
        // 마지막 페이지의 마지막 행을 지우면 빈 페이지가 남습니다.
        if (this.tableData.length === 1 && this.currentPage > 1) this.currentPage -= 1;
        this.closeFreeDeleteModal();
        this.getData();
        this.getStats();
      } catch (error) {
        const status = apiErrorStatus(error);
        const detail = error.response?.data?.detail;
        // 아직 배포되지 않은 API. 라우트가 없으면 FastAPI 가 detail: "Not Found" 로 답하고,
        // 구현 후의 404 는 백엔드가 한글 문구를 채워주기로 했습니다.
        // (docs/결제_주문_영수증_시스템.md §무료/사은품 삭제 API — 요청서 원본은 완료 후 삭제됐습니다)
        if (status === 405 || status === 501 || (status === 404 && (!detail || detail === "Not Found"))) {
          console.info("무료/사은품 삭제 API 미배포: DELETE /payment/admin/freePayment");
          this.$notifyVue({
            type: "warning",
            title: "삭제 API가 아직 준비되지 않았습니다",
            text: "백엔드에 요청한 DELETE /payment/admin/freePayment 가 배포되면 바로 동작합니다.",
          });
          this.closeFreeDeleteModal();
          return;
        }
        if (status === 404) {
          // 이미 지워진 행입니다. 목록만 새로 고치면 사라지므로 오류가 아닙니다.
          this.$notifyVue({ type: "warning", title: "이미 삭제된 내역입니다.", text: "목록을 새로 고쳤습니다." });
          this.closeFreeDeleteModal();
          this.getData();
          this.getStats();
          return;
        }
        if (status === 409) {
          // 백엔드가 거부한 경우입니다(영수증·주문 연결 / 환불됨 / 유료 / 경쟁상태).
          // 재시도해도 결과가 같으므로 실패가 아니라 안내로 띄우고 목록을 최신화합니다.
          this.$notifyVue({
            type: "warning", title: "삭제할 수 없습니다",
            text: apiErrorMessage(error, "관리자 무료지급 건인지 확인해 주세요."),
          });
          this.closeFreeDeleteModal();
          this.getData();
          this.getStats();
          return;
        }
        console.error("무료/사은품 삭제 실패:", error);
        this.$notifyVue({
          type: "danger", title: "삭제 실패",
          text: apiErrorMessage(error, "다시 시도해 주세요."),
        });
      } finally {
        this.freeDeleteLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.psl-page {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__stats {
    display: grid;
    // "활성 기능 이용권" 카드가 조건부라 고정 열 수로는 4장·5장 두 상태를 동시에 만족시킬 수 없습니다.
    // repeat(5, 1fr) 은 1200px 대에서 통화 값(28px mono, nowrap)이 카드 밖으로 삐져나옵니다.
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: 16px;
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__stat-note {
    display: block;
    color: $p-err;
    font-size: 10px;
    font-weight: 700;
    margin-top: 4px;

    // 기본형은 환율 경고용 빨강입니다. 단순 부연 설명에는 이 변형을 씁니다.
    &--muted { color: $p-ink-soft; font-weight: 600; }
  }
  // KPI 카드의 순액 병기. 숫자라 모노 폰트, 설명은 툴팁으로.
  &__stat-net {
    display: block; margin-top: 2px;
    font-family: $p-font-mono; font-size: 11px; font-weight: 700; color: $p-ink-mid;
    cursor: help;
  }

  &__tabs-row {
    display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  }
  &__tabs { align-self: flex-start; }
  &__view-switch { margin-left: auto; }

  // ---- 달력 뷰 ----
  &__cal { display: flex; flex-direction: column; gap: 12px; }
  &__cal-nav {
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  }
  &__cal-month { font-size: 15px; font-weight: 800; color: $p-ink; min-width: 110px; text-align: center; }
  &__cal-summary {
    display: inline-flex; align-items: baseline; gap: 10px; flex-wrap: wrap;
    margin-left: auto; font-size: 12px; color: $p-ink-mid;
    > strong { font-weight: 800; color: $p-ink; }
    &--muted { color: $p-ink-soft; }
  }
  &__cal-summary-item { display: inline-flex; align-items: baseline; gap: 6px; white-space: nowrap; }
  &__cal-summary-amt { font-family: $p-font-mono; font-weight: 800; color: $p-ink; }
  &__cal-summary-refund { font-family: $p-font-mono; color: $p-ink-soft; font-size: 11px; }
  &__cal-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    overflow: hidden;
    background: $p-line; // 셀 사이 1px 선은 배경색이 비쳐서 만들어집니다
    gap: 1px;
  }
  &__cal-dow {
    padding: 7px 0; text-align: center;
    background: $p-panel-alt; color: $p-ink-mid;
    font-size: 11px; font-weight: 800;
    &:first-child { color: $p-err-ink; }
    &:nth-child(7) { color: $p-info-ink; }
  }
  &__cal-cell {
    min-height: 92px;
    padding: 6px 8px;
    background: $p-panel;
    display: flex; flex-direction: column; gap: 2px;
    font-size: 11px; line-height: 1.35;
    transition: background $p-dur $p-ease;
    &.is-out { background: $p-panel-alt; .psl-page__cal-date { color: $p-ink-softer; } }
    &.is-today .psl-page__cal-date {
      background: $p-ink; color: $p-point; border-radius: 999px;
    }
    &.has-data { cursor: pointer; }
    &.has-data:hover { background: $p-point-soft; }
  }
  &__cal-date {
    align-self: flex-start;
    min-width: 22px; height: 22px; padding: 0 6px;
    display: inline-flex; align-items: center; justify-content: center;
    font-family: $p-font-mono; font-weight: 700; color: $p-ink-mid; font-size: 12px;
  }
  &__cal-count { font-weight: 800; color: $p-ink; margin-top: 2px; }
  &__cal-amt {
    font-family: $p-font-mono; font-weight: 700; color: $p-point-deep;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  &__cal-more { color: $p-ink-soft; font-weight: 700; }
  &__cal-refund { color: $p-err-ink; font-weight: 700; font-family: $p-font-mono; line-height: 1.4; }
  &__tab-icon { font-size: 14px !important; line-height: 1; }

  &__sec-title {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 800; color: $p-ink;
  }

  &__exclude-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    margin: 0;
    border: 1px solid $p-line;
    border-radius: 999px;
    background: $p-panel;
    color: $p-ink-mid;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all $p-dur $p-ease;

    > input { cursor: pointer; margin: 0; }
    > i { font-size: 15px !important; color: $p-ink-soft; }

    &:hover { border-color: $p-point-deep; color: $p-point-deep; }

    &:has(input:checked) {
      background: $p-ink;
      color: $p-point;
      border-color: $p-ink;

      > i { color: $p-point; }
    }
  }
  &__sec-icon { color: $p-point-deep; font-size: 16px !important; }

  &__num { font-family: $p-font-mono; color: $p-ink-mid; font-weight: 700; }
  &__learner-cell {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }

  &__learner { font-weight: 700; color: $p-ink; }

  // 같은 이름의 다른 계정을 구분하는 단서라 잘리면 의미가 없습니다. 줄바꿈을 허용합니다.
  &__learner-email {
    font-family: $p-font-mono;
    font-size: 10px;
    color: $p-ink-soft;
    word-break: break-all;
    line-height: 1.3;
  }

  // 모달 요약행의 이름 옆에 붙는 이메일
  &__inline-email {
    display: block;
    margin-top: 2px;
    font-family: $p-font-mono;
    font-size: 10px;
    font-weight: 600;
    color: $p-ink-soft;
    word-break: break-all;
  }

  &__textbook {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__textbook-title {
    font-weight: 700;
    color: $p-ink;
    font-size: 12px;
  }

  &__mono { font-family: $p-font-mono; color: $p-ink-soft; font-size: 11px; }
  &__order-id { color: $p-err; font-family: $p-font-mono; font-size: 10px; }

  &__amount-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
  }

  &__amount { font-family: $p-font-mono; font-weight: 800; color: $p-ink; }
  &__discount { color: $p-ok; font-family: $p-font-mono; font-size: 10px; font-weight: 700; }
  // 업그레이드 차감. 쿠폰 할인과 같은 줄에 서지만 성격이 달라 색을 나눕니다.
  &__credited { color: $p-info; font-family: $p-font-mono; font-size: 10px; font-weight: 700; }

  &__method-icon { font-size: 11px !important; margin-right: 2px; }
  &__method-note { display: block; margin-top: 2px; font-size: 10px; color: $p-ink-soft; }

  &__period {
    display: flex;
    flex-direction: column;
    gap: 2px;

    > small { color: $p-ink-soft; font-family: $p-font-mono; font-size: 10px; }
  }

  // 영구는 날짜가 아니라 상태라서 색으로 한 번 더 구분합니다.
  &__period-perm { color: $p-ok !important; font-weight: 800; }
  &__period-note { color: $p-info !important; font-family: $p-font-body !important; }
  &__period-feature { color: $p-ink-mid !important; font-family: $p-font-body !important; }
  // 묶음 구매의 기능만 끝난 행. 도서는 살아 있으므로 경고색은 쓰지 않습니다.
  &__period-ended { color: $p-ink-soft !important; font-family: $p-font-body !important; font-style: italic; }

  &__product {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
  }

  &__muted { color: $p-ink-soft; }

  &__actions {
    display: inline-flex;
    gap: 4px;
    justify-content: flex-end;
  }

  // 잠긴 액션 버튼의 툴팁 앵커. disabled 버튼은 이벤트를 내지 않으므로 포인터를 span 이 받습니다.
  &__act-locked {
    display: inline-grid;
    cursor: not-allowed;

    > .p-icon-btn {
      pointer-events: none;
      opacity: 0.45;
    }
  }

  // ── 환불 ──
  &__status-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
  }

  &__refund-at {
    font-family: $p-font-mono;
    font-size: 10px;
    color: $p-ink-soft;
  }

  &__refund-amount {
    font-family: $p-font-mono;
    font-size: 10px;
    font-weight: 700;
    color: $p-err;
  }

  // LS 세전 표시. 금액 옆에 붙되 금액보다 약해야 해서 테두리만 두른 무채색으로 둡니다.
  &__refund-pretax {
    margin-left: 4px;
    padding: 0 3px;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    font-size: 10px;
    font-weight: 700;
    color: $p-ink-mid;
    cursor: help;
  }

  // 반영 중인 행의 sync 아이콘. 버튼 자체는 disabled 라 클릭이 겹치지 않습니다.
  &__sync-spin {
    animation: p-spin 0.9s linear infinite;
  }

  // refund_status 를 무시하는(미배포) 서버를 만났을 때만 뜹니다
  &__totals {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 18px;
    margin-bottom: 12px;
    padding: 9px 14px;
    border-radius: $p-radius-md;
    background: $p-panel;
    border: 1px solid $p-line;
    font-size: 12px;
    line-height: 1.4;
  }
  &__totals-label {
    display: inline-flex; align-items: center; gap: 5px;
    font-weight: 800; color: $p-ink-mid;
    > i { font-size: 16px !important; color: $p-point-deep; }
  }
  &__totals-item {
    display: inline-flex; align-items: baseline; gap: 6px; flex-wrap: wrap;
  }
  &__totals-amount { font-family: $p-font-mono; font-weight: 800; color: $p-ink; font-size: 13px; }
  &__totals-count { color: $p-ink-soft; font-weight: 700; }
  &__totals-refund { color: $p-ink-mid; font-family: $p-font-mono; white-space: nowrap; }

  &__filter-warn {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: $p-radius-md;
    background: $p-warn-soft;
    border: 1px solid $p-warn;
    color: $p-warn-ink;
    font-size: 12px;
    line-height: 1.5;

    > i { font-size: 18px !important; color: $p-warn; flex-shrink: 0; }
    strong { font-weight: 800; }
    code { font-family: $p-font-mono; font-size: 11px; }
  }

  // 환불 즉시 반영 결과 헤더. 4분기를 색으로도 구분합니다 —
  // 특히 unknown(확인 불가)이 none(환불 아님)과 같은 색으로 보이면 안 됩니다.
  &__sync-head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    border-radius: $p-radius-md;
    border: 1px solid $p-line;
    background: $p-panel-alt;

    > i { font-size: 22px !important; color: $p-ink-soft; flex-shrink: 0; }

    strong { display: block; font-size: 14px; font-weight: 800; color: $p-ink; }
    p { margin: 4px 0 0; font-size: 12px; line-height: 1.5; color: $p-ink-mid; }

    &.is-applied {
      background: $p-ok-soft;
      border-color: $p-ok;
      > i { color: $p-ok; }
      strong { color: $p-ok-ink; }
    }

    &.is-already {
      background: $p-info-soft;
      border-color: $p-info;
      > i { color: $p-info; }
      strong { color: $p-info-ink; }
    }

    // 판정 불가는 결론이 아니라 "아직 모른다" 입니다. 결론처럼 보이는 색을 쓰지 않습니다.
    &.is-unknown {
      border-style: dashed;
      border-color: $p-line-hard;
    }
  }

  // force 재반영. 평소에는 접혀 있어야 하는 탈출구입니다.
  &__advanced {
    border: 1px dashed $p-line-hard;
    border-radius: $p-radius-md;
    padding: 10px 12px;

    > summary {
      cursor: pointer;
      font-size: 12px;
      font-weight: 700;
      color: $p-ink-mid;
    }

    > p {
      margin: 10px 0;
      font-size: 12px;
      line-height: 1.5;
      color: $p-ink-mid;
    }
  }

  // ── 모달 공통 ──
  &__modal-title {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 15px; font-weight: 800; color: $p-ink;
  }
  &__modal-icon {
    color: $p-point-deep;
    font-size: 20px !important;

    &--danger { color: $p-err; }
  }

  // ── Toss 환불 모달 ──
  &__cancel {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__cancel-summary {
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__cancel-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: $p-ink-mid;

    > strong { color: $p-ink; font-weight: 800; }

    &--highlight {
      padding-top: 8px;
      border-top: 1px solid $p-line;
      margin-top: 4px;

      > strong { color: $p-err; font-family: $p-font-mono; }
    }
  }

  &__cancel-warn {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border-radius: $p-radius-md;
    background: $p-warn-soft;
    border: 1px solid $p-warn;
    color: $p-warn-ink;
    font-size: 12px;
    line-height: 1.5;

    > i { font-size: 18px !important; color: $p-warn; flex-shrink: 0; }
    strong { font-weight: 800; }

    // 복구 불가를 알리는 상시 경고
    &--hard {
      background: $p-err-soft;
      border-color: $p-err;
      color: $p-err-ink;

      > i { color: $p-err; }
    }

    // 경고가 아니라 "이렇게 동작한다"는 사실 안내
    &--info {
      background: $p-info-soft;
      border-color: $p-info;
      color: $p-info-ink;

      > i { color: $p-info; }
    }
  }

  // 안내 블록 안에서 한 단계 낮은 덧붙임. 본문과 붙여 두면 두 사실이 한 문장으로 읽힙니다.
  // 색을 흐리지 않는 이유: 12px 한글 본문이라 opacity 를 걸면 *-ink 토큰이 지키는 AA 대비가
  // 깨집니다($p-info-ink 5.34:1 → 85% 합성 시 3.96:1). 구분은 선으로만 줍니다.
  &__cancel-note {
    display: block;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px dashed currentColor;
  }

  // 환불 순서 가드 안내 (정책 §6-3)
  &__guard {
    display: flex;
    flex-direction: column;
    gap: 8px;

    > p { margin: 0; }
  }

  // 서버가 준 detail 원문. 운영자가 그대로 CS 에 옮겨 쓰는 문장이라 인용처럼 세워 둡니다.
  &__guard-detail {
    padding: 8px 10px;
    background: $p-panel;
    border-left: 3px solid $p-err;
    border-radius: $p-radius-sm;
    color: $p-ink-mid;
    font-size: 12px;
    line-height: 1.6;
    white-space: pre-line;
  }

  &__guard-code {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    > code {
      padding: 3px 8px;
      background: $p-panel;
      border: 1px solid $p-line;
      border-radius: $p-radius-sm;
      font-family: $p-font-mono;
      font-size: 12px;
      color: $p-ink;
      // 결제 코드는 길어서 좁은 모달에서 줄을 넘깁니다. 잘라내면 옮겨 적을 수 없습니다.
      word-break: break-all;
    }
  }

  // 동반 환불 대상 목록
  &__related {
    list-style: none;
    margin: 8px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;

    > li {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px;
      padding: 6px 8px;
      background: $p-panel;
      border: 1px solid $p-line-soft;
      border-radius: $p-radius-sm;
      font-size: 11px;
      color: $p-ink-mid;

      > small { width: 100%; color: $p-err; font-weight: 700; }
    }
  }

  &__counter {
    display: block;
    text-align: right;
    font-size: 10px;
    color: $p-ink-soft;
    margin-top: 2px;
    font-family: $p-font-mono;
  }

  &__bank-info {
    background: $p-warn-soft;
    border-left: 3px solid $p-warn;
    border-radius: $p-radius-md;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__bank-label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 800;
    color: $p-warn;
    margin-bottom: 4px;

    > i { font-size: 14px !important; }
  }

  // ── PG 수수료 ──
  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 36px 24px;
    color: $p-ink-mid;

    p { margin: 0; font-size: 12px; }
  }

  &__fees {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__fee-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
  }

  &__fee-pg {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__fee-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: grid;
    place-items: center;
    background: $p-panel;
    border: 1px solid $p-line;

    > i { font-size: 18px !important; color: $p-ink2; }
    > img { width: 24px; height: 24px; object-fit: contain; }
  }

  &__fee-name {
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
  }

  &__fee-rate {
    font-family: $p-font-mono;
    font-weight: 800;
    color: $p-point-deep;
    font-size: 14px;
  }

  // ── 무료/사은품 등록 모달 ──
  &__free {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  &__free-section {
    padding: 14px 16px;
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    display: flex;
    flex-direction: column;
    gap: 10px;

    // 값이 무시되는 구간을 흐리게만 합니다. 입력을 막지는 않습니다 —
    // 등급을 다시 바꾸면 그대로 쓰이므로 값을 지우거나 잠그면 오히려 불편합니다.
    &.is-dimmed { opacity: 0.55; }
  }

  &__free-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid $p-line-soft;
  }

  &__free-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: grid;
    place-items: center;
    flex-shrink: 0;

    > i { font-size: 18px !important; }

    &--info { background: $p-info-soft; color: $p-info; }
    &--ok { background: $p-ok-soft; color: $p-ok; }
    &--warn { background: $p-warn-soft; color: $p-warn; }
    &--point { background: $p-point-glow; color: $p-point-deep; }
  }

  &__grade-picker {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__grade-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
    padding: 10px 12px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    background: $p-panel;
    cursor: pointer;
    text-align: left;
    font-family: $p-font-body;
    transition: all $p-dur $p-ease;

    > strong { font-size: 12px; font-weight: 800; color: $p-ink; }
    > small { font-size: 10px; line-height: 1.4; color: $p-ink-soft; }

    &:hover { border-color: $p-point-deep; }

    &.is-active {
      background: $p-ink;
      border-color: $p-ink;

      > strong { color: $p-point; }
      > small { color: $p-line-soft; }
    }
  }

  &__free-grade-loading {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 2px;
    font-size: 11px;
    color: $p-ink-mid;
  }

  &__free-head-text {
    flex: 1;
    min-width: 0;

    > label {
      display: block;
      font-size: 12px;
      font-weight: 800;
      color: $p-ink;
    }
    > small {
      display: block;
      margin-top: 2px;
      font-size: 11px;
      color: $p-ink-soft;
    }
  }

  &__dynamic-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__row-idx {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: $p-bg;
    color: $p-point-deep;
    display: inline-grid;
    place-items: center;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    flex-shrink: 0;
  }

  &__plan-input {
    display: flex;
    align-items: stretch;
    border: 1px solid $p-line;
    border-radius: $p-radius;
    overflow: hidden;
    background: $p-panel;

    &:focus-within {
      border-color: $p-point-deep;
      box-shadow: 0 0 0 3px $p-point-glow;
    }

    > input {
      flex: 1;
      border: 0 !important;
      box-shadow: none !important;
    }
  }

  &__plan-unit {
    padding: 0 12px;
    background: $p-bg;
    border-left: 1px solid $p-line;
    display: inline-grid;
    place-items: center;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink2;
  }

  &__plan-quick {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  &__plan-chip {
    padding: 6px 12px;
    border: 1px solid $p-line;
    background: $p-panel;
    color: $p-ink-mid;
    border-radius: 999px;
    cursor: pointer;
    font-family: $p-font-body;
    font-size: 11px;
    font-weight: 700;
    transition: all $p-dur $p-ease;

    &:hover {
      border-color: $p-point-deep;
      color: $p-point-deep;
    }

    &.is-active {
      background: $p-ink;
      color: $p-point;
      border-color: $p-ink;
    }
  }

  &__free-fixed {
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
    padding: 12px 14px;
  }

  &__free-fixed-head {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
    color: $p-ink-mid;
    font-size: 11px;

    > i { font-size: 14px !important; color: $p-ink-soft; }
    > span { font-weight: 800; color: $p-ink2; }
    > small { color: $p-ink-soft; }
  }

  &__free-fixed-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__free-fixed-note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: $p-radius-sm;
    background: $p-info-soft;
    color: $p-info-ink;
    font-size: 11px;
    line-height: 1.5;

    > i { font-size: 15px !important; color: $p-info; flex-shrink: 0; }
    strong { font-weight: 800; }

    // 아직 상품을 고를 수 없는 상태(개편 배포 전)
    &--warn {
      background: $p-warn-soft;
      color: $p-warn-ink;

      > i { color: $p-warn; }
    }
  }

  &__free-result {
    padding: 14px 16px;
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__free-result-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 10px;
    border-bottom: 1px solid $p-line-soft;

    > i { font-size: 18px !important; color: $p-point-deep; }
    > span { font-size: 12px; font-weight: 800; color: $p-ink; margin-right: auto; }
  }

  &__free-result-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 10px;
    border-radius: $p-radius-sm;
    border-left: 3px solid $p-line;
    background: $p-panel-alt;

    > i { font-size: 16px !important; flex-shrink: 0; margin-top: 1px; }

    &.is-ok   { border-left-color: $p-ok;   background: $p-ok-soft;   > i { color: $p-ok; } }
    &.is-warn { border-left-color: $p-warn; background: $p-warn-soft; > i { color: $p-warn; } }
    &.is-err  { border-left-color: $p-err;  background: $p-err-soft;  > i { color: $p-err; } }
  }

  &__free-result-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;

    > strong { font-size: 12px; font-weight: 800; color: $p-ink; }
    > small { font-size: 11px; color: $p-ink-soft; }
    > span { font-size: 11px; color: $p-ink-mid; font-weight: 700; }
  }

  &__free-result-id {
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink-soft;
    flex-shrink: 0;
  }

  &__fixed-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    background: $p-panel;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-sm;
    font-size: 11px;

    > span { color: $p-ink-soft; font-family: $p-font-mono; }
    > strong { color: $p-ink; font-weight: 800; font-family: $p-font-mono; }
  }
}
</style>
